import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import redditApi from '../../../services/redditApi';
import RhDatePicker from './RhDatePicker';
import ThreadHealth from '../components/ThreadHealth';

// ─── CSV Upload Modal ─────────────────────────────────────────────────────────

function parseCSVPreview(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [], error: 'CSV appears empty.' };
  const splitLine = (l) => {
    const cols = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; continue; }
      cur += ch;
    }
    cols.push(cur.trim());
    return cols;
  };
  const headers = splitLine(lines[0]);
  const required = ['Client', 'URL', 'Post Title', 'Post Date', 'Proposed Comment'];
  const missing = required.filter((r) => !headers.includes(r));
  if (missing.length) return { headers, rows: [], error: `Missing column(s): ${missing.join(', ')}` };
  const rows = lines.slice(1).map((l) => {
    const vals = splitLine(l);
    return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? '']));
  }).filter((r) => r['Client'] || r['URL'] || r['Proposed Comment']);
  return { headers, rows, error: null };
}

/** Local calendar date as YYYY-MM-DD (avoids UTC shift from toISOString). */
function toLocalYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format an ISO timestamp as YYYY-MM-DD HH:MM AM/PM; missing → em dash. */
function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = toLocalYMD(d);
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${date} ${time}`;
}

/** Statuses where the redditor has actually posted the comment. */
const POSTED_IDENTITY_STATUSES = new Set(['submitted', 'live', 'deleted', 'dws']);

/**
 * Reddit account used to post, only for comments that were submitted.
 * Scheduled / overdue / skipped / cancelled rows stay empty.
 */
function postedAccountUsername(comment) {
  if (!POSTED_IDENTITY_STATUSES.has(comment?.status)) return '';
  return comment.reddit_account_username || '';
}

/** Start of today in local time. */
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** YYYY-MM-DD of the first weekday strictly after the given date. */
function nextWeekdayAfter(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return toLocalYMD(d);
}

/**
 * Pivot for new schedules: if lastScheduled is in the past (or today), continue
 * after today; if it is in the future, continue after that date.
 */
function scheduleStartAfterLast(lastScheduledDate) {
  const today = startOfToday();
  if (!lastScheduledDate) return nextWeekdayAfter(today);
  const last = new Date(lastScheduledDate);
  last.setHours(0, 0, 0, 0);
  if (Number.isNaN(last.getTime()) || last <= today) {
    return nextWeekdayAfter(today);
  }
  return nextWeekdayAfter(last);
}

function CSVUploadModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [commentsPerMonth, setCommentsPerMonth] = useState(20);
  const [customMonthInput, setCustomMonthInput] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [startDate, setStartDate] = useState(() => nextWeekdayAfter(startOfToday()));
  const fileRef = useRef(null);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Load organizations for the org dropdown
  useEffect(() => {
    redditApi.get('/admin/organizations/').then(({ data }) => setOrganizations(data)).catch(() => {});
  }, []);

  // Redditor assignment state
  const [redditors, setRedditors] = useState([]);
  // clientMeta: { [clientName]: { isNew: bool, existingName: str|null } }
  const [clientMeta, setClientMeta] = useState({});
  // newClientRedditors: { [clientName]: string[] } — only for new clients
  const [newClientRedditors, setNewClientRedditors] = useState({});

  function handleFile(f) {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setParseError('Please select a .csv file.');
      setFile(null); setPreview(null);
      return;
    }
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows, error } = parseCSVPreview(e.target.result);
      setPreview({ headers, rows });
      setParseError(error);
    };
    reader.readAsText(f);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }

  // Classify CSV clients as existing (locked) vs new (redditor picker) once preview is ready
  useEffect(() => {
    if (!preview || preview.rows.length === 0 || parseError) {
      setClientMeta({});
      setRedditors([]);
      setNewClientRedditors({});
      return;
    }
    redditApi.get('/admin/clients/').then(({ data }) => {
      const redditorList = data.redditors || [];
      setRedditors(redditorList);

      const existingMap = {};
      (data.clients || []).forEach((c) => {
        existingMap[c.client_name.toLowerCase()] = {
          assignedName: (c.assigned_redditors || []).map((r) => r.name).filter(Boolean).join(', ') || c.assigned_redditor_name,
          assignedId: c.assigned_redditor_id,
          lastScheduledDate: c.last_scheduled_date,
        };
      });

      const uniqueClients = [...new Set(preview.rows.map((r) => r['Client']).filter(Boolean))];
      const meta = {};
      const initialSelections = {};

      uniqueClients.forEach((name) => {
        const existing = existingMap[name.toLowerCase()];
        if (existing) {
          meta[name] = { isNew: false, existingName: existing.assignedName };
        } else {
          meta[name] = { isNew: true };
          // No auto-selection — admin must explicitly choose a redditor.
        }
      });

      setClientMeta(meta);
      setNewClientRedditors(initialSelections);

      // Auto-fill start date from the latest last-scheduled date among existing
      // clients in this CSV. Past/overdue last dates pivot from today; future
      // last dates continue after that date. New-client-only uploads keep the
      // default "next weekday after today".
      const existingDates = uniqueClients
        .map((name) => existingMap[name.toLowerCase()]?.lastScheduledDate)
        .filter(Boolean)
        .map((s) => new Date(`${s}T00:00:00`))
        .filter((d) => !Number.isNaN(d.getTime()));
      if (existingDates.length > 0) {
        const maxDate = new Date(Math.max(...existingDates.map((d) => d.getTime())));
        setStartDate(scheduleStartAfterLast(maxDate));
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, parseError]);

  const totalRows = preview?.rows?.length ?? 0;

  // Recompute schedule whenever totalRows, commentsPerMonth, or startDate change.
  // Matches backend: comments_per_day = max(1, round(comments_per_month / 22))
  // so 20/mo → 1/day, 40/mo → 2/day on weekdays.
  const scheduleCalc = React.useMemo(() => {
    if (!totalRows || !commentsPerMonth || !startDate) return null;
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const isWeekday = (d) => d.getDay() !== 0 && d.getDay() !== 6;
    const commentsPerDay = Math.max(1, Math.round(commentsPerMonth / 22));

    // Never preview from a past date — match backend (pivot after today).
    const floor = nextWeekdayAfter(startOfToday());
    const effectiveStart = startDate < floor ? floor : startDate;
    let start = new Date(effectiveStart + 'T00:00:00');
    while (!isWeekday(start)) start.setDate(start.getDate() + 1);

    // Pack up to commentsPerDay comments per weekday (same as _build_publishing_dates)
    let current = new Date(start);
    let placed = 0;
    while (placed < totalRows) {
      if (isWeekday(current)) {
        for (let i = 0; i < commentsPerDay && placed < totalRows; i++) {
          placed++;
        }
      }
      if (placed < totalRows) current.setDate(current.getDate() + 1);
    }

    return {
      startLabel: fmt(start),
      endLabel: fmt(current),
      commentsPerDay,
    };
  }, [totalRows, commentsPerMonth, startDate]);

  // All new clients must have a redditor chosen before upload is enabled
  const allNewClientsAssigned = Object.entries(clientMeta)
    .filter(([, meta]) => meta.isNew)
    .every(([name]) => (newClientRedditors[name] || []).length > 0);

  // Whether this upload introduces any brand-new client. The Organization
  // selector is only relevant for new clients — existing ones keep their
  // saved organization on the backend.
  const hasNewClient = Object.values(clientMeta).some((meta) => meta.isNew);

  async function handleUpload() {
    if (!file || parseError || !allNewClientsAssigned) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('comments_per_month', commentsPerMonth);
    fd.append('start_date', startDate);
    fd.append('new_client_redditors', JSON.stringify(newClientRedditors));
    if (selectedOrgId) fd.append('organization_id', selectedOrgId);
    try {
      const { data } = await redditApi.post('/admin/csv-upload/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      onUploaded();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  }

  const PREVIEW_COLS = ['#', 'Client', 'Post Title', 'Post Date', 'Proposed Comment'];

  return (
    <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 740, width: '95%' }}>
        <div className="rh-modal-header">
          <h3 className="rh-modal-title">Add Comments via CSV</h3>
          <button className="rh-modal-close" onClick={onClose}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="rh-modal-body" style={{ padding: '24px 28px' }}>
          {!result ? (
            <>
              {/* Drop zone */}
              <div
                className={`rh-csv-dropzone${dragging ? ' rh-csv-dropzone--over' : ''}${file && !parseError ? ' rh-csv-dropzone--loaded' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFile(e.target.files[0])}
                />
                {file && !parseError ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <svg width="32" height="32" fill="none" stroke="var(--rh-orange)" strokeWidth="1.8" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="9" y1="15" x2="15" y2="15"/>
                      <line x1="9" y1="11" x2="15" y2="11"/>
                    </svg>
                    <span style={{ fontWeight: 600, color: 'var(--rh-text)' }}>{file.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>{preview?.rows?.length ?? 0} rows detected — click to change</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <svg width="36" height="36" fill="none" stroke="var(--rh-text-faint)" strokeWidth="1.6" viewBox="0 0 24 24">
                      <polyline points="16 16 12 12 8 16"/>
                      <line x1="12" y1="12" x2="12" y2="21"/>
                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                    </svg>
                    <span style={{ fontWeight: 600, color: 'var(--rh-text)' }}>Drop CSV here or click to browse</span>
                    <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>Required columns: #, Client, URL, Post Title, Post Date, Proposed Comment</span>
                  </div>
                )}
              </div>

              {parseError && (
                <p style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>⚠ {parseError}</p>
              )}

              {/* Preview table */}
              {preview && preview.rows.length > 0 && !parseError && (
                <div style={{ marginTop: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--rh-text-muted)', marginBottom: 8 }}>
                    Preview — first 5 rows of {preview.rows.length}
                  </p>
                  <div className="rh-table-wrap" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    <table className="rh-table">
                      <thead>
                        <tr>
                          {PREVIEW_COLS.map((c) => <th key={c}>{c}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.slice(0, 5).map((row, i) => (
                          <tr key={i}>
                            <td className="rh-text-sm rh-text-muted">{row['#'] || i + 1}</td>
                            <td className="rh-font-medium">{row['Client']}</td>
                            <td className="rh-text-sm rh-text-truncate" style={{ maxWidth: 160 }}>
                              <a className="rh-link" href={row['URL']} target="_blank" rel="noreferrer">
                                {row['Post Title']}
                              </a>
                            </td>
                            <td className="rh-text-sm">{row['Post Date']}</td>
                            <td className="rh-text-sm rh-text-muted rh-text-truncate" style={{ maxWidth: 200 }}>
                              {row['Proposed Comment'].slice(0, 90)}{row['Proposed Comment'].length > 90 ? '…' : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Schedule settings — shown once a valid file is loaded */}
              {preview && preview.rows.length > 0 && !parseError && (
                <div style={{ marginTop: 20, borderTop: '1px solid var(--rh-border)', paddingTop: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--rh-text)', marginBottom: 4 }}>
                    Posting Schedule
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', marginBottom: 14 }}>
                    Choose cadence (~1/2 days for 10/mo, ~1/day for 20/mo, ~2/day for 40/mo on weekdays) and when to start.
                  </p>

                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
                  }}>
                    {/* Comments per month */}
                    <div style={{
                      padding: '14px 16px', borderRadius: 10,
                      border: '1px solid var(--rh-border)', background: '#fafafa',
                    }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', marginBottom: 10 }}>
                        Comments per month
                      </p>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {[
                          { n: 10, label: '~1/2 days' },
                          { n: 20, label: '~1/day' },
                          { n: 40, label: '~2/day' },
                        ].map(({ n, label }) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => { setCommentsPerMonth(n); setCustomMonthInput(''); }}
                            style={{
                              minWidth: 56, height: 42, padding: '0 8px',
                              borderRadius: 9,
                              border: commentsPerMonth === n && !customMonthInput ? '2px solid var(--rh-orange)' : '2px solid var(--rh-border)',
                              background: commentsPerMonth === n && !customMonthInput ? 'var(--rh-orange)' : '#fff',
                              color: commentsPerMonth === n && !customMonthInput ? '#fff' : 'var(--rh-text)',
                              fontWeight: 700, fontSize: 13,
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              flexShrink: 0,
                              display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center',
                              lineHeight: 1.15,
                            }}
                          >
                            <span>{n}</span>
                            <span style={{
                              fontSize: 9, fontWeight: 600, opacity: 0.85,
                            }}>{label}</span>
                          </button>
                        ))}
                        <input
                          type="number"
                          min="1"
                          max="200"
                          placeholder="Custom"
                          value={customMonthInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCustomMonthInput(val);
                            const parsed = parseInt(val, 10);
                            if (!isNaN(parsed) && parsed > 0) setCommentsPerMonth(parsed);
                          }}
                          style={{
                            height: 42, width: 100,
                            borderRadius: 9,
                            border: customMonthInput ? '2px solid var(--rh-orange)' : '2px solid var(--rh-border)',
                            padding: '0 10px',
                            fontSize: 13, fontWeight: 600,
                            color: 'var(--rh-text)',
                            background: customMonthInput ? 'rgba(255,69,0,0.04)' : '#fff',
                            outline: 'none',
                            fontFamily: 'inherit',
                          }}
                        />
                      </div>
                    </div>

                    {/* Start date */}
                    <div style={{
                      padding: '14px 16px', borderRadius: 10,
                      border: '1px solid var(--rh-border)', background: '#fafafa',
                    }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', marginBottom: 10 }}>
                        Start date
                      </p>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{
                          border: '1.5px solid var(--rh-border)',
                          borderRadius: 8,
                          padding: '9px 12px',
                          fontSize: 13,
                          color: 'var(--rh-text)',
                          outline: 'none',
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          height: 42,
                          boxSizing: 'border-box',
                          width: '100%',
                          background: '#fff',
                        }}
                      />
                    </div>
                  </div>

                  {/* Organization assignment — only for uploads with a new client */}
                  {organizations.length > 0 && hasNewClient && (
                    <div style={{ marginTop: 12, padding: '14px 16px', borderRadius: 10, border: '1px solid var(--rh-border)', background: '#fafafa' }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', marginBottom: 6 }}>
                        Organization
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--rh-text-faint)', marginBottom: 10 }}>
                        Assigned to all <strong>new</strong> clients in this upload. Existing clients keep their current organization.
                      </p>
                      <select
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                        style={{
                          width: '100%', padding: '8px 10px', fontSize: 13,
                          border: '1.5px solid var(--rh-border)', borderRadius: 8,
                          background: '#fff', color: 'var(--rh-text)', cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <option value="">— Select organization (optional) —</option>
                        {organizations.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Schedule summary pill */}
                  {scheduleCalc && (
                    <div style={{
                      marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
                      background: 'rgba(255,69,0,0.07)', border: '1px solid rgba(255,69,0,0.18)',
                      borderRadius: 10, padding: '10px 14px', fontSize: 13,
                    }}>
                      <svg width="14" height="14" fill="none" stroke="var(--rh-orange)" strokeWidth="2.2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <span>
                        <strong style={{ color: 'var(--rh-orange)' }}>{totalRows} comments</strong>
                        <span style={{ color: 'var(--rh-text-muted)' }}> · {commentsPerMonth}/month (~{scheduleCalc.commentsPerDay}/day)</span>
                        <span style={{ color: 'var(--rh-text-muted)' }}> · starts </span>
                        <strong style={{ color: 'var(--rh-text)' }}>{scheduleCalc.startLabel}</strong>
                        <span style={{ color: 'var(--rh-text-muted)' }}> · ends </span>
                        <strong style={{ color: 'var(--rh-text)' }}>{scheduleCalc.endLabel}</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Redditor Assignment per client */}
              {Object.keys(clientMeta).length > 0 && (
                <div style={{ marginTop: 20, borderTop: '1px solid var(--rh-border)', paddingTop: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--rh-text)', marginBottom: 4 }}>
                    Redditor Assignment
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', marginBottom: 14 }}>
                    Existing clients keep their assigned redditors. Select one or more for any new client; comments are split round-robin.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Object.entries(clientMeta).map(([clientName, meta]) => (
                      <div
                        key={clientName}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 14px', borderRadius: 10,
                          border: `1px solid ${meta.isNew ? 'rgba(255,69,0,0.25)' : 'var(--rh-border)'}`,
                          background: meta.isNew ? 'rgba(255,69,0,0.03)' : '#fafafa',
                        }}
                      >
                        <span style={{ flex: 1, fontWeight: 600, fontSize: 13, color: 'var(--rh-text)' }}>
                          {clientName}
                        </span>

                        {meta.isNew ? (
                          <>
                            <span style={{
                              fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                              color: 'var(--rh-orange)', background: 'rgba(255,69,0,0.12)',
                              borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap',
                            }}>
                              New client
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, maxWidth: 280 }}>
                              <div style={{
                                display: 'flex', flexDirection: 'column', gap: 4, width: '100%',
                                border: `1.5px solid ${(newClientRedditors[clientName] || []).length ? 'var(--rh-orange)' : '#dc2626'}`,
                                borderRadius: 8, padding: '8px 10px', background: '#fff',
                                maxHeight: 160, overflowY: 'auto',
                              }}>
                                {redditors.map((r) => {
                                  const checked = (newClientRedditors[clientName] || []).includes(r.id);
                                  return (
                                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => setNewClientRedditors((prev) => {
                                          const current = prev[clientName] || [];
                                          return {
                                            ...prev,
                                            [clientName]: checked
                                              ? current.filter((id) => id !== r.id)
                                              : [...current, r.id],
                                          };
                                        })}
                                      />
                                      {r.name}
                                    </label>
                                  );
                                })}
                              </div>
                              {!(newClientRedditors[clientName] || []).length && (
                                <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 500 }}>
                                  Required — select at least one
                                </span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <svg width="14" height="14" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <span style={{ color: '#16a34a', fontWeight: 600 }}>
                              {meta.existingName || 'Assigned'}
                            </span>
                            <span style={{ color: 'var(--rh-text-faint)', fontSize: 11 }}>(locked)</span>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : result.error ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <svg width="48" height="48" fill="none" stroke="#dc2626" strokeWidth="1.6" viewBox="0 0 24 24" style={{ display: 'block', margin: '0 auto 12px' }}>
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              <p style={{ fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>Upload failed</p>
              <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>{result.error}</p>
            </div>
          ) : (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <svg width="48" height="48" fill="none" stroke="var(--rh-orange)" strokeWidth="1.6" viewBox="0 0 24 24" style={{ display: 'block', margin: '0 auto 12px' }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <p style={{ fontWeight: 700, fontSize: 18, color: 'var(--rh-text)', marginBottom: 4 }}>
                  {result.created} comment{result.created !== 1 ? 's' : ''} assigned
                </p>
                {result.schedule && (
                  <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', marginBottom: 4 }}>
                    {result.schedule.comments_per_month}/month · {result.schedule.start_date} → {result.schedule.end_date}
                  </p>
                )}
                {result.errors?.length > 0 && (
                  <p style={{ fontSize: 13, color: '#b45309' }}>
                    {result.errors.length} row{result.errors.length !== 1 ? 's' : ''} had errors (see below)
                  </p>
                )}
              </div>

              {/* Per-redditor summary */}
              {result.assignments?.length > 0 && (() => {
                const byRedditor = {};
                result.assignments.forEach(({ assigned_to }) => {
                  byRedditor[assigned_to] = (byRedditor[assigned_to] || 0) + 1;
                });
                return (
                  <div style={{ marginBottom: result.errors?.length ? 16 : 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                      Assignment breakdown
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {Object.entries(byRedditor).map(([name, count]) => (
                        <span key={name} style={{ background: 'var(--rh-orange-light)', color: 'var(--rh-orange-dark)', borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 600 }}>
                          {name} · {count}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Errors */}
              {result.errors?.length > 0 && (
                <div style={{ marginTop: 12, background: '#fef2f2', borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>Row errors</p>
                  {result.errors.map((e) => (
                    <p key={e.row} style={{ fontSize: 12, color: '#b91c1c', marginBottom: 2 }}>Row {e.row}: {e.error}</p>
                  ))}
                </div>
              )}

              {/* Rejected — invalid threads (not scheduled) */}
              {(result.rejected_invalid || result.rejected_archived)?.length > 0 && (
                <div style={{ marginTop: 12, background: '#fffbeb', borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginBottom: 6 }}>
                    {(result.rejected_invalid || result.rejected_archived).length} thread{(result.rejected_invalid || result.rejected_archived).length !== 1 ? 's' : ''} skipped — not commentable
                  </p>
                  {(result.rejected_invalid || result.rejected_archived).map((r, i) => (
                    <div key={r.row ?? r.reddit_url ?? i} style={{ fontSize: 12, color: '#92400e', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {r.thread_status && (
                          <span className={`rh-badge rh-badge-thread-${r.thread_status}`}>{r.thread_status}</span>
                        )}
                        <a className="rh-link" href={r.reddit_url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                          {r.post_title || r.reddit_url}
                        </a>
                      </div>
                      {r.reason && <div style={{ color: '#a16207', marginTop: 2 }}>{r.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="rh-modal-footer">
          {!result ? (
            <>
              <button className="rh-btn rh-btn-secondary" onClick={onClose}>Cancel</button>
              <button
                className="rh-btn rh-btn-primary"
                disabled={!file || !!parseError || uploading || !allNewClientsAssigned}
                onClick={handleUpload}
              >
                {uploading ? 'Uploading…' : `Upload & Assign${preview?.rows ? ` (${preview.rows.length} rows)` : ''}`}
              </button>
            </>
          ) : (
            <button className="rh-btn rh-btn-primary" onClick={onClose} style={{ marginLeft: 'auto' }}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Select({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selected = options.find((o) => o.value === value);

  function openMenu() {
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 180);
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuHeight = Math.min(options.length * 40 + 8, 260);
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;

    // If menu would overflow the right edge, right-align it to the trigger
    const wouldOverflowRight = rect.left + menuWidth > window.innerWidth - 12;
    const horizontalStyle = wouldOverflowRight
      ? { right: window.innerWidth - rect.right, left: 'auto' }
      : { left: rect.left };

    setMenuStyle({
      position: 'fixed',
      width: menuWidth,
      zIndex: 9999,
      ...horizontalStyle,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    }
    function handleScroll() { setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <div className={`rh-custom-select${open ? ' rh-custom-select--open' : ''}`}>
      <button
        type="button"
        ref={triggerRef}
        className="rh-custom-select-trigger"
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <svg
          className="rh-custom-select-chevron"
          width="12" height="12" fill="none" stroke="currentColor"
          strokeWidth="2.5" viewBox="0 0 24 24"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="rh-custom-select-menu" ref={menuRef} style={menuStyle}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`rh-custom-select-option${value === o.value ? ' selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {value === o.value && (
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const POLL_MS = 15_000;

const STATUS_TOOLTIPS = {
  scheduled: 'The comment is planned to be posted at a selected date/time.',
  overdue:   'The scheduled posting time has passed, but the comment has not been posted yet.',
  submitted: 'The comment has been sent/submitted to Reddit for posting.',
  live:      'The comment has been successfully posted and is visible on Reddit.',
  deleted:   'The comment was removed/deleted from Reddit after being posted.',
  archived:  'The comment/task is saved in history and is no longer active.',
  dws:       'The comment was deleted immediately after posting.',
  skipped:   'The Redditor could not post this comment and skipped it. It was never posted and requires manual review before any replacement.',
  cancelled: 'An admin cancelled this assignment. It was never posted, is not billable, and is hidden from the Redditor todo. It can be restored.',
};

const SKIP_REASON_LABELS = {
  bot_bouncer:         'Bot bouncer present',
  duplicate:           'Previously posted by someone else/Duplicate comment',
  old_thread:          'Old thread',
  comments_filtered:   'Comments being filtered',
  unfit_brand:         'Post is unfit for a brand mention',
  brand_promo_blocked: 'Brand promotion not allowed',
  mod_active_review:   'Moderator is active and manually reviewing comments',
  other:               'Other',
};

function RhTooltip({ tip, children }) {
  const [coords, setCoords] = useState(null);
  const handleEnter = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setCoords({ x: r.left + r.width / 2, y: r.top });
  };
  const handleLeave = () => setCoords(null);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {children}
      {coords && (
        <span style={{
          position: 'fixed',
          left: coords.x,
          top: coords.y - 8,
          transform: 'translate(-50%, -100%)',
          background: '#1e293b',
          color: '#f8fafc',
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1.45,
          padding: '6px 10px',
          borderRadius: 7,
          whiteSpace: 'pre-wrap',
          maxWidth: 240,
          width: 'max-content',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 99999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}>
          {tip}
        </span>
      )}
    </span>
  );
}

function StatusBadge({ status, isOverdue, submittedUrl, skipReason, redditorNote, cancelledByName, cancelledAt }) {
  if (status === 'skipped') {
    const reasonLabel = SKIP_REASON_LABELS[skipReason] || 'Unknown';
    let tip = `Skipped — ${reasonLabel}`;
    if (redditorNote?.trim()) tip += `\nRemarks: ${redditorNote.trim()}`;
    return (
      <RhTooltip tip={tip}>
        <span className="rh-badge rh-badge-skipped">Skipped</span>
      </RhTooltip>
    );
  }
  if (status === 'cancelled') {
    let tip = STATUS_TOOLTIPS.cancelled;
    if (cancelledByName) tip += `\nCancelled by ${cancelledByName}`;
    if (cancelledAt) tip += `\n${new Date(cancelledAt).toLocaleString()}`;
    return (
      <RhTooltip tip={tip}>
        <span className="rh-badge rh-badge-cancelled">Cancelled</span>
      </RhTooltip>
    );
  }
  if (isOverdue && status === 'scheduled') {
    return (
      <RhTooltip tip={STATUS_TOOLTIPS.overdue}>
        <span className="rh-badge rh-badge-overdue">Overdue</span>
      </RhTooltip>
    );
  }
  if ((status === 'submitted' || status === 'live') && submittedUrl) {
    const badgeCls = status === 'live' ? 'rh-badge-live' : 'rh-badge-submitted';
    return (
      <RhTooltip tip={STATUS_TOOLTIPS[status]}>
        <a
          href={submittedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`rh-badge ${badgeCls}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
        >
          {status}
          <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </a>
      </RhTooltip>
    );
  }
  const cls = {
    submitted: 'rh-badge-submitted',
    live: 'rh-badge-live',
    scheduled: 'rh-badge-scheduled',
    deleted: 'rh-badge-deleted',
    archived: 'rh-badge-archived',
    dws: 'rh-badge-dws',
    cancelled: 'rh-badge-cancelled',
  }[status] || 'rh-badge-scheduled';
  return (
    <RhTooltip tip={STATUS_TOOLTIPS[status]}>
      <span className={`rh-badge ${cls}`}>
        {status === 'dws' ? 'Instant Deletion' : status}
      </span>
    </RhTooltip>
  );
}

function LiveBadge() {
  return (
    <span className="rh-live-badge">
      <span className="rh-live-dot" />
      Live
    </span>
  );
}

export default function OverviewTab({ stats, loading, onRefresh, user, selectedClient, assignedRedditor, assignedRedditorId }) {
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const STATUS_OPTIONS = [
    { value: 'scheduled',  label: 'Scheduled' },
    { value: 'overdue',    label: 'Overdue' },
    { value: 'submitted',  label: 'Submitted' },
    { value: 'live',       label: 'Live' },
    { value: 'deleted',    label: 'Deleted' },
    { value: 'archived',   label: 'Archived' },
    { value: 'dws',        label: 'Instant Deletion' },
    { value: 'skipped',    label: 'Skipped' },
    { value: 'cancelled',  label: 'Cancelled' },
  ];
  const DEFAULT_ACTIVE = new Set(['scheduled', 'overdue', 'submitted', 'live', 'archived', 'skipped']);

  // Load persisted status preference for this user, fall back to default
  const statusStorageKey = `rh_status_filter_${user?.email || 'default'}`;
  function loadSavedStatuses() {
    try {
      const saved = localStorage.getItem(statusStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch { /* ignore */ }
    return new Set(DEFAULT_ACTIVE);
  }

  const [activeStatuses, setActiveStatuses] = useState(() => loadSavedStatuses());
  const [statusDropOpen, setStatusDropOpen]  = useState(false);
  const statusDropRef = useRef(null);

  // Persist whenever selection changes
  useEffect(() => {
    try {
      localStorage.setItem(statusStorageKey, JSON.stringify([...activeStatuses]));
    } catch { /* ignore */ }
  }, [activeStatuses, statusStorageKey]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (statusDropRef.current && !statusDropRef.current.contains(e.target)) {
        setStatusDropOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function toggleStatus(val) {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  }

  const [clientFilter] = useState(selectedClient || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Edit posted comment (Submitted / Deleted / DWS) — also reused by the
  // developer "Mark as Submitted" flow (postEditMode === 'submit').
  const [postEditTarget, setPostEditTarget] = useState(null);
  const [postEditMode, setPostEditMode] = useState('edit'); // 'edit' | 'submit'
  const [postEditFields, setPostEditFields] = useState({});
  const [postEditSaving, setPostEditSaving] = useState(false);
  const [postEditAccounts, setPostEditAccounts] = useState([]);
  const [postEditAccountsLoading, setPostEditAccountsLoading] = useState(false);
  const [postEditLivenessRan, setPostEditLivenessRan] = useState(false);
  const [postEditLivenessDeleted, setPostEditLivenessDeleted] = useState(false);
  const [postEditWasDws, setPostEditWasDws] = useState(false);
  const [postEditWasSkipped, setPostEditWasSkipped] = useState(false);

  const filtersRef = useRef({ clientFilter, dateFrom, dateTo });
  useEffect(() => {
    filtersRef.current = { clientFilter, dateFrom, dateTo };
  }, [clientFilter, dateFrom, dateTo]);

  // Client-side status filtering for All Assignments table
  const filteredComments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return comments.filter(c => {
      if (c.status === 'scheduled') {
        const pub = c.publishing_date ? new Date(c.publishing_date) : null;
        const isOverdue = pub && pub < today;
        return isOverdue ? activeStatuses.has('overdue') : activeStatuses.has('scheduled');
      }
      return activeStatuses.has(c.status);
    });
  }, [comments, activeStatuses]);

  const isDeveloper = user?.custom_role === 'Developer';
  const isAdmin = !!user?.is_admin;

  // Shareable report link
  const [shareToken, setShareToken] = useState(null);
  const [shareFetching, setShareFetching] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareRegenerating, setShareRegenerating] = useState(false);
  const [shareDropOpen, setShareDropOpen] = useState(false);
  const shareDropRef = useRef(null);

  useEffect(() => {
    if (!shareDropOpen) return;
    function onClickOutside(e) {
      if (shareDropRef.current && !shareDropRef.current.contains(e.target)) {
        setShareDropOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [shareDropOpen]);

  async function handleGetShareLink() {
    if (!selectedClient) return;
    if (shareToken && shareDropOpen) { setShareDropOpen(false); return; }
    if (shareToken) { setShareDropOpen(true); return; }
    setShareFetching(true);
    try {
      const { data } = await redditApi.get(`/admin/share-token/?client=${encodeURIComponent(selectedClient)}`);
      const fullUrl = `${window.location.origin}${data.share_url}`;
      setShareToken({ ...data, full_url: fullUrl });
      setShareDropOpen(true);
    } catch {
      // silent fail
    } finally {
      setShareFetching(false);
    }
  }

  async function handleCopyShareLink() {
    if (!shareToken) return;
    try {
      await navigator.clipboard.writeText(shareToken.full_url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch { /* silent */ }
  }

  async function handleRegenerateShareLink() {
    if (!selectedClient || !shareToken) return;
    setShareRegenerating(true);
    try {
      const { data } = await redditApi.post('/admin/share-token/', { client_name: selectedClient });
      const fullUrl = `${window.location.origin}${data.share_url}`;
      setShareToken({ ...data, full_url: fullUrl });
    } catch {
      // silent fail
    } finally {
      setShareRegenerating(false);
    }
  }

  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showLivenessConfirm, setShowLivenessConfirm] = useState(false);
  const [livenessStatuses, setLivenessStatuses] = useState(new Set(['submitted', 'live']));
  const [livenessClient, setLivenessClient] = useState('');
  const [livenessClientList, setLivenessClientList] = useState([]);
  const [livenessPreview, setLivenessPreview] = useState(null);
  const [livenessDispatching, setLivenessDispatching] = useState(false);
  // Background task state — liveness
  const [livenessTaskId, setLivenessTaskId] = useState(null);
  const [livenessTaskDone, setLivenessTaskDone] = useState(false);
  const [livenessTaskResult, setLivenessTaskResult] = useState(null);
  const livenessPollerRef = useRef(null);

  // Backfill state
  const [showBackfillConfirm, setShowBackfillConfirm] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillDispatching, setBackfillDispatching] = useState(false);
  const [backfillTaskId, setBackfillTaskId] = useState(null);
  const [backfillTaskDone, setBackfillTaskDone] = useState(false);
  const [backfillTaskResult, setBackfillTaskResult] = useState(null);
  const backfillPollerRef = useRef(null);

  // Backfill Submitted At state
  const [showSubmittedAtBackfillConfirm, setShowSubmittedAtBackfillConfirm] = useState(false);
  const [submittedAtBackfillPreview, setSubmittedAtBackfillPreview] = useState(null);
  const [submittedAtBackfillDispatching, setSubmittedAtBackfillDispatching] = useState(false);
  const [submittedAtBackfillTaskId, setSubmittedAtBackfillTaskId] = useState(null);
  const [submittedAtBackfillTaskDone, setSubmittedAtBackfillTaskDone] = useState(false);
  const [submittedAtBackfillTaskResult, setSubmittedAtBackfillTaskResult] = useState(null);
  const submittedAtBackfillPollerRef = useRef(null);

  // Send Reminders state
  const [showSendRemindersConfirm, setShowSendRemindersConfirm] = useState(false);
  const [sendRemindersPreview, setSendRemindersPreview] = useState(null);
  const [sendRemindersSending, setSendRemindersSending] = useState(false);
  const [sendRemindersResult, setSendRemindersResult] = useState(null);

  // Recheck Accounts state
  const [showRecheckConfirm, setShowRecheckConfirm] = useState(false);
  const [recheckPreview, setRecheckPreview] = useState(null);
  const [recheckDispatching, setRecheckDispatching] = useState(false);
  const [recheckTaskId, setRecheckTaskId] = useState(null);
  const [recheckTaskDone, setRecheckTaskDone] = useState(false);
  const [recheckTaskResult, setRecheckTaskResult] = useState(null);
  const recheckPollerRef = useRef(null);

  // Check Banned Accounts state
  const [showBannedCheckConfirm, setShowBannedCheckConfirm] = useState(false);
  const [bannedCheckPreview, setBannedCheckPreview] = useState(null);
  const [bannedCheckDispatching, setBannedCheckDispatching] = useState(false);
  const [bannedCheckTaskId, setBannedCheckTaskId] = useState(null);
  const [bannedCheckTaskDone, setBannedCheckTaskDone] = useState(false);
  const [bannedCheckTaskResult, setBannedCheckTaskResult] = useState(null);
  const bannedCheckPollerRef = useRef(null);

  // Check Niche state
  const [showCheckNicheConfirm, setShowCheckNicheConfirm] = useState(false);
  const [checkNichePreview, setCheckNichePreview] = useState(null);
  const [checkNicheDispatching, setCheckNicheDispatching] = useState(false);
  const [checkNicheTaskId, setCheckNicheTaskId] = useState(null);
  const [checkNicheTaskDone, setCheckNicheTaskDone] = useState(false);
  const [checkNicheTaskResult, setCheckNicheTaskResult] = useState(null);
  const checkNichePollerRef = useRef(null);

  // Sync Client Data (NocoDB) state
  const [showSyncNocodbConfirm, setShowSyncNocodbConfirm] = useState(false);
  const [syncNocodbPreview, setSyncNocodbPreview] = useState(null);
  const [syncNocodbDispatching, setSyncNocodbDispatching] = useState(false);
  const [syncNocodbTaskId, setSyncNocodbTaskId] = useState(null);
  const [syncNocodbTaskDone, setSyncNocodbTaskDone] = useState(false);
  const [syncNocodbTaskResult, setSyncNocodbTaskResult] = useState(null);
  const syncNocodbPollerRef = useRef(null);

  // Archive check state
  const [showArchiveCheckConfirm, setShowArchiveCheckConfirm] = useState(false);
  const [archiveCheckMode, setArchiveCheckMode] = useState('future'); // 'future' | 'past'
  const [archiveCheckDays, setArchiveCheckDays] = useState(14);
  const [archiveCheckClient, setArchiveCheckClient] = useState('');
  const [archiveCheckPreview, setArchiveCheckPreview] = useState(null);
  const [archiveCheckDispatching, setArchiveCheckDispatching] = useState(false);
  const [archiveCheckTaskId, setArchiveCheckTaskId] = useState(null);
  const [archiveCheckTaskDone, setArchiveCheckTaskDone] = useState(false);
  const [archiveCheckTaskResult, setArchiveCheckTaskResult] = useState(null);
  const archiveCheckPollerRef = useRef(null);

  // Fill quota shortfalls (dev)
  const [showFillQuotaConfirm, setShowFillQuotaConfirm] = useState(false);
  const [fillQuotaClient, setFillQuotaClient] = useState('');
  const [fillQuotaClientList, setFillQuotaClientList] = useState([]);
  const [fillQuotaPreview, setFillQuotaPreview] = useState(null);
  const [fillQuotaRunning, setFillQuotaRunning] = useState(false);
  const [fillQuotaResult, setFillQuotaResult] = useState(null);

  // Pull Assignments (selected client → current month)
  const [showPullAssignmentsConfirm, setShowPullAssignmentsConfirm] = useState(false);
  const [pullAssignmentsCount, setPullAssignmentsCount] = useState(1);
  const [pullAssignmentsRunning, setPullAssignmentsRunning] = useState(false);
  const [pullAssignmentsResult, setPullAssignmentsResult] = useState(null);
  const [pullAssignmentsPreview, setPullAssignmentsPreview] = useState(null);

  // Reset Overdue state
  const [showResetOverdueConfirm, setShowResetOverdueConfirm] = useState(false);
  const [resetOverduePreview, setResetOverduePreview] = useState(null);
  const [resetOverdueRunning, setResetOverdueRunning] = useState(false);
  const [resetOverdueResult, setResetOverdueResult] = useState(null);

  async function openResetOverdueConfirm() {
    setShowActionsMenu(false);
    setResetOverduePreview(null);
    setResetOverdueResult(null);
    setShowResetOverdueConfirm(true);
    try {
      const params = assignedRedditorId ? `?redditor_id=${assignedRedditorId}` : '';
      const { data } = await redditApi.get(`/admin/dev/reset-overdue/${params}`);
      setResetOverduePreview(data);
    } catch (err) {
      setResetOverduePreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmResetOverdue() {
    if (!assignedRedditorId) return;
    setResetOverdueRunning(true);
    try {
      const { data } = await redditApi.post('/admin/dev/reset-overdue/', { redditor_id: assignedRedditorId });
      setResetOverdueResult({ ok: true, ...data });
    } catch (err) {
      setResetOverdueResult({ ok: false, error: err.response?.data?.detail || 'Reset failed.' });
    } finally {
      setResetOverdueRunning(false);
    }
  }

  // Close actions menu when clicking outside
  useEffect(() => {
    if (!showActionsMenu) return;
    function handleClickOutside(e) {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionsMenu]);

  async function confirmClearAll() {
    setShowClearConfirm(false);
    setClearing(true);
    try {
      await redditApi.delete(`/admin/dev/clear/?client=${encodeURIComponent(selectedClient || '')}`);
      fetchComments(true);
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Clear failed.');
    } finally {
      setClearing(false);
    }
  }

  async function openLivenessConfirm() {
    setShowActionsMenu(false);
    setLivenessPreview(null);
    const defaultStatuses = new Set(['submitted', 'live']);
    setLivenessStatuses(defaultStatuses);
    setLivenessClient('');
    setShowLivenessConfirm(true);
    // Fetch full client list and preview in parallel
    try {
      const [clientRes, previewRes] = await Promise.all([
        redditApi.get('/admin/clients/'),
        redditApi.get('/admin/dev/liveness-check/?statuses=submitted&statuses=live'),
      ]);
      const names = (clientRes.data.clients || [])
        .map((c) => c.client_name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setLivenessClientList(names);
      setLivenessPreview(previewRes.data);
    } catch (err) {
      setLivenessPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function refreshLivenessPreview(statuses, client) {
    setLivenessPreview(null);
    try {
      const params = new URLSearchParams();
      statuses.forEach((s) => params.append('statuses', s));
      if (client) params.set('client', client);
      const { data } = await redditApi.get(`/admin/dev/liveness-check/?${params}`);
      setLivenessPreview(data);
    } catch (err) {
      setLivenessPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  function toggleLivenessStatus(statusKey) {
    setLivenessStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(statusKey)) next.delete(statusKey); else next.add(statusKey);
      refreshLivenessPreview([...next], livenessClient);
      return next;
    });
  }

  function setLivenessClientAndRefresh(client) {
    setLivenessClient(client);
    refreshLivenessPreview([...livenessStatuses], client);
  }

  async function confirmLivenessCheck() {
    setLivenessDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/liveness-check/', {
        statuses: [...livenessStatuses],
        client: livenessClient,
      });
      setShowLivenessConfirm(false);
      setLivenessTaskId(data.task_id);
    } catch (err) {
      setLivenessPreview({ error: err.response?.data?.detail || 'Failed to start liveness check.' });
    } finally {
      setLivenessDispatching(false);
    }
  }

  async function openBackfillConfirm() {
    setShowActionsMenu(false);
    setBackfillPreview(null);
    setBackfillTaskResult(null);
    setShowBackfillConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/backfill/');
      setBackfillPreview(data);
    } catch (err) {
      setBackfillPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmBackfill() {
    setBackfillDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/backfill/');
      setShowBackfillConfirm(false);
      setBackfillTaskId(data.task_id);
    } catch (err) {
      setBackfillPreview({ error: err.response?.data?.detail || 'Failed to start backfill.' });
    } finally {
      setBackfillDispatching(false);
    }
  }

  async function openSubmittedAtBackfillConfirm() {
    setShowActionsMenu(false);
    setSubmittedAtBackfillPreview(null);
    setSubmittedAtBackfillTaskResult(null);
    setShowSubmittedAtBackfillConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/backfill-submitted-at/');
      setSubmittedAtBackfillPreview(data);
    } catch (err) {
      setSubmittedAtBackfillPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmSubmittedAtBackfill() {
    setSubmittedAtBackfillDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/backfill-submitted-at/');
      setShowSubmittedAtBackfillConfirm(false);
      setSubmittedAtBackfillTaskId(data.task_id);
    } catch (err) {
      setSubmittedAtBackfillPreview({ error: err.response?.data?.detail || 'Failed to start submitted-at backfill.' });
    } finally {
      setSubmittedAtBackfillDispatching(false);
    }
  }

  async function openRecheckConfirm() {
    setShowActionsMenu(false);
    setRecheckPreview(null);
    setRecheckTaskResult(null);
    setRecheckTaskDone(false);
    setShowRecheckConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/recheck-accounts/');
      setRecheckPreview(data);
    } catch (err) {
      setRecheckPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmRecheck() {
    setRecheckDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/recheck-accounts/');
      setShowRecheckConfirm(false);
      setRecheckTaskId(data.task_id);
    } catch (err) {
      setRecheckPreview({ error: err.response?.data?.detail || 'Failed to start re-check.' });
    } finally {
      setRecheckDispatching(false);
    }
  }

  async function openBannedCheckConfirm() {
    setShowActionsMenu(false);
    setBannedCheckPreview(null);
    setBannedCheckTaskResult(null);
    setBannedCheckTaskDone(false);
    setShowBannedCheckConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/check-banned-accounts/');
      setBannedCheckPreview(data);
    } catch (err) {
      setBannedCheckPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmBannedCheck() {
    setBannedCheckDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/check-banned-accounts/', {});
      setShowBannedCheckConfirm(false);
      setBannedCheckTaskId(data.task_id);
    } catch (err) {
      setBannedCheckPreview({ error: err.response?.data?.detail || 'Failed to start banned-account check.' });
    } finally {
      setBannedCheckDispatching(false);
    }
  }

  async function openCheckNicheConfirm() {
    setShowActionsMenu(false);
    setCheckNichePreview(null);
    setCheckNicheTaskResult(null);
    setCheckNicheTaskDone(false);
    setShowCheckNicheConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/check-niche/');
      setCheckNichePreview(data);
    } catch (err) {
      setCheckNichePreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmCheckNiche() {
    setCheckNicheDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/check-niche/');
      setShowCheckNicheConfirm(false);
      setCheckNicheTaskId(data.task_id);
    } catch (err) {
      setCheckNichePreview({ error: err.response?.data?.detail || 'Failed to start niche check.' });
    } finally {
      setCheckNicheDispatching(false);
    }
  }

  async function openSyncNocodbConfirm() {
    setShowActionsMenu(false);
    setSyncNocodbPreview(null);
    setSyncNocodbTaskResult(null);
    setSyncNocodbTaskDone(false);
    setShowSyncNocodbConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/sync-nocodb-clients/');
      setSyncNocodbPreview(data);
    } catch (err) {
      setSyncNocodbPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmSyncNocodb() {
    setSyncNocodbDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/sync-nocodb-clients/');
      setShowSyncNocodbConfirm(false);
      setSyncNocodbTaskId(data.task_id);
    } catch (err) {
      setSyncNocodbPreview({ error: err.response?.data?.detail || 'Failed to start client sync.' });
    } finally {
      setSyncNocodbDispatching(false);
    }
  }

  async function openSendRemindersConfirm() {
    setShowActionsMenu(false);
    setSendRemindersPreview(null);
    setSendRemindersResult(null);
    setShowSendRemindersConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/send-reminders/');
      setSendRemindersPreview(data);
    } catch (err) {
      setSendRemindersPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmSendReminders() {
    setSendRemindersSending(true);
    try {
      const { data } = await redditApi.post('/admin/dev/send-reminders/');
      setShowSendRemindersConfirm(false);
      setSendRemindersResult(data);
    } catch (err) {
      setSendRemindersPreview({ error: err.response?.data?.detail || 'Failed to send reminders.' });
    } finally {
      setSendRemindersSending(false);
    }
  }

  async function openFillQuotaConfirm() {
    setShowActionsMenu(false);
    setFillQuotaPreview(null);
    setFillQuotaResult(null);
    const initialClient = selectedClient || '';
    setFillQuotaClient(initialClient);
    setShowFillQuotaConfirm(true);
    try {
      const [allRes, previewRes] = await Promise.all([
        redditApi.get('/admin/dev/fill-quota-shortfalls/'),
        redditApi.get(
          initialClient
            ? `/admin/dev/fill-quota-shortfalls/?client=${encodeURIComponent(initialClient)}`
            : '/admin/dev/fill-quota-shortfalls/',
        ),
      ]);
      setFillQuotaClientList((allRes.data.clients || []).map((c) => c.client_name));
      setFillQuotaPreview(previewRes.data);
    } catch (err) {
      setFillQuotaPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function refreshFillQuotaPreview(client) {
    setFillQuotaPreview(null);
    try {
      const params = client ? `?client=${encodeURIComponent(client)}` : '';
      const { data } = await redditApi.get(`/admin/dev/fill-quota-shortfalls/${params}`);
      setFillQuotaPreview(data);
    } catch (err) {
      setFillQuotaPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmFillQuotaShortfalls() {
    setFillQuotaRunning(true);
    setFillQuotaResult(null);
    try {
      const body = fillQuotaClient ? { client: fillQuotaClient } : {};
      const { data } = await redditApi.post('/admin/dev/fill-quota-shortfalls/', body);
      setFillQuotaResult(data);
      fetchComments?.(true);
      onRefresh?.();
    } catch (err) {
      setFillQuotaResult({ error: err.response?.data?.detail || 'Fill failed.' });
    } finally {
      setFillQuotaRunning(false);
    }
  }

  async function openPullAssignmentsConfirm() {
    if (!clientFilter) {
      alert('Select a client first.');
      return;
    }
    setPullAssignmentsCount(1);
    setPullAssignmentsResult(null);
    setPullAssignmentsPreview(null);
    setShowPullAssignmentsConfirm(true);
    try {
      const { data } = await redditApi.get(
        `/admin/pull-assignments/?client=${encodeURIComponent(clientFilter)}`,
      );
      setPullAssignmentsPreview(data);
      const available = data.future_available ?? 0;
      setPullAssignmentsCount(available > 0 ? Math.min(1, available) : 1);
    } catch (err) {
      setPullAssignmentsPreview({
        error: err.response?.data?.detail || 'Could not load future scheduled count.',
      });
    }
  }

  async function confirmPullAssignments() {
    if (!clientFilter) return;
    const count = Number(pullAssignmentsCount);
    if (!Number.isInteger(count) || count < 1) {
      setPullAssignmentsResult({ error: 'Enter a positive whole number.' });
      return;
    }
    setPullAssignmentsRunning(true);
    setPullAssignmentsResult(null);
    try {
      const { data } = await redditApi.post('/admin/pull-assignments/', {
        client: clientFilter,
        count,
      });
      setPullAssignmentsResult(data);
      fetchComments?.(true);
      onRefresh?.();
    } catch (err) {
      setPullAssignmentsResult({ error: err.response?.data?.detail || 'Pull failed.' });
    } finally {
      setPullAssignmentsRunning(false);
    }
  }

  async function openArchiveCheckConfirm() {
    setShowActionsMenu(false);
    setArchiveCheckPreview(null);
    setArchiveCheckTaskResult(null);
    setArchiveCheckTaskDone(false);
    setArchiveCheckClient('');
    setArchiveCheckDays(14);
    setArchiveCheckMode('future');
    setShowArchiveCheckConfirm(true);
    try {
      const { data } = await redditApi.get('/admin/dev/archive-check/?days=14&mode=future');
      setArchiveCheckPreview(data);
    } catch (err) {
      setArchiveCheckPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function refreshArchiveCheckPreview(days, client, mode) {
    setArchiveCheckPreview(null);
    try {
      const params = new URLSearchParams({ days, mode: mode || 'future' });
      if (client) params.set('client', client);
      const { data } = await redditApi.get(`/admin/dev/archive-check/?${params.toString()}`);
      setArchiveCheckPreview(data);
    } catch (err) {
      setArchiveCheckPreview({ error: err.response?.data?.detail || 'Could not load preview.' });
    }
  }

  async function confirmArchiveCheck() {
    setArchiveCheckDispatching(true);
    try {
      const { data } = await redditApi.post('/admin/dev/archive-check/', {
        days: archiveCheckDays,
        client: archiveCheckClient,
        dry_run: false,
        mode: archiveCheckMode,
      });
      setShowArchiveCheckConfirm(false);
      setArchiveCheckTaskId(data.task_id);
    } catch (err) {
      setArchiveCheckPreview({ error: err.response?.data?.detail || 'Failed to start archive check.' });
    } finally {
      setArchiveCheckDispatching(false);
    }
  }

  async function confirmDeleteComment() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await redditApi.delete(`/admin/comments/${deleteTarget.id}/`);
      if (!data?.id) {
        alert('Cancel did not complete. The API may still be running old code — refresh and try again after restart.');
        return;
      }
      setComments((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      setActiveStatuses((prev) => new Set([...prev, 'cancelled']));
      setDeleteTarget(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Cancel failed.');
    } finally {
      setDeleting(false);
    }
  }

  async function restoreCancelledComment(comment) {
    if (!comment || restoringId) return;
    setRestoringId(comment.id);
    try {
      const { data } = await redditApi.post(`/admin/comments/${comment.id}/restore/`);
      setComments((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      if (viewTarget?.id === data.id) setViewTarget(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Restore failed.');
    } finally {
      setRestoringId(null);
    }
  }

  function openEdit(c) {
    setEditTarget(c);
    setEditFields({
      comment_text: c.comment_text,
      publishing_date: c.publishing_date || '',
    });
  }

  async function saveEdit(e) {
    e.preventDefault();
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const { data } = await redditApi.patch(`/admin/comments/${editTarget.id}/`, editFields);
      setComments((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      setEditTarget(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed.');
    } finally {
      setEditSaving(false);
    }
  }

  async function openPostEdit(c, mode = 'edit') {
    setPostEditTarget(c);
    setPostEditMode(mode);
    setPostEditLivenessRan(false);
    setPostEditLivenessDeleted(false);
    setPostEditWasDws(false);
    setPostEditWasSkipped(false);
    setPostEditFields({
      submitted_url: mode === 'submit' ? '' : (c.submitted_url || ''),
      reddit_account_id: c.reddit_account_id || '',
      deleted_while_submitting: false,
      skipped: false,
      skip_reason: '',
      redditor_note: '',
    });
    setPostEditAccounts([]);
    if (c.redditor_id) {
      setPostEditAccountsLoading(true);
      try {
        const { data } = await redditApi.get(`/admin/redditors/${c.redditor_id}/accounts/`);
        setPostEditAccounts(data || []);
      } catch {
        setPostEditAccounts([]);
      } finally {
        setPostEditAccountsLoading(false);
      }
    }
  }

  async function savePostEdit(e) {
    e.preventDefault();
    if (!postEditTarget) return;
    setPostEditSaving(true);
    setPostEditLivenessRan(false);
    setPostEditLivenessDeleted(false);
    setPostEditWasDws(false);
    setPostEditWasSkipped(false);
    try {
      const payload = postEditMode === 'submit' && postEditFields.skipped
        ? {
            skipped: true,
            skip_reason: postEditFields.skip_reason,
            redditor_note: (postEditFields.redditor_note || '').trim(),
          }
        : postEditFields;
      const { data } = postEditMode === 'submit'
        ? await redditApi.post(`/admin/comments/${postEditTarget.id}/submit/`, payload)
        : await redditApi.patch(`/admin/comments/${postEditTarget.id}/`, postEditFields);
      setComments((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      onRefresh?.();
      if (postEditMode === 'submit') {
        setPostEditLivenessRan(true);
        setPostEditWasDws(!!data.is_dws);
        setPostEditWasSkipped(!!data.is_skipped);
        setPostEditLivenessDeleted(!!data.liveness_deleted);
      } else if (data.liveness_check_run) {
        setPostEditLivenessRan(true);
      } else {
        setPostEditTarget(null);
      }
    } catch (err) {
      const d = err.response?.data;
      alert(d?.detail || d?.skip_reason?.[0] || d?.redditor_note?.[0] || 'Save failed.');
    } finally {
      setPostEditSaving(false);
    }
  }

  function exportAssignmentsCSV() {
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = isAdmin
      ? ['Redditor', 'Account Used', 'Post Title', 'Subreddit', 'Thread Created', 'Comment Preview', 'Comment Date', 'Status', 'Days Live', 'Submitted URL']
      : ['Redditor', 'Post Title', 'Subreddit', 'Thread Created', 'Comment Preview', 'Comment Date', 'Status', 'Days Live', 'Submitted URL'];
    const rows = filteredComments.map((c) => {
      const accountUsername = postedAccountUsername(c);
      const base = [
        escape(c.redditor_name || ''),
        escape(c.post_title || ''),
        escape(c.subreddit ? (c.subreddit.startsWith('r/') ? c.subreddit : `r/${c.subreddit}`) : ''),
        escape(c.post_date || ''),
        escape(c.comment_text || ''),
        escape(c.publishing_date || ''),
        escape(c.is_overdue && c.status === 'scheduled' ? 'overdue' : (c.status === 'dws' ? 'instant deletion' : (c.status || ''))),
        escape(c.days_live != null ? c.days_live : ''),
        escape(c.submitted_url || ''),
      ];
      if (isAdmin) {
        base.splice(1, 0, escape(accountUsername ? `u/${accountUsername}` : ''));
      }
      return base.join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const now = new Date();
    const clientPrefix = clientFilter ? `${clientFilter.replace(/\s+/g, '_')}_` : '';
    a.download = `${clientPrefix}RedditHub_Assignments_${String(now.getDate()).padStart(2,'0')}_${String(now.getMonth()+1).padStart(2,'0')}_${now.getFullYear()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const fetchComments = useCallback((showSpinner = false) => {
    const { clientFilter, dateFrom, dateTo } = filtersRef.current;
    const params = new URLSearchParams();
    if (clientFilter) params.set('client', clientFilter);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (showSpinner) setLoadingComments(true);
    redditApi.get(`/admin/comments/?${params.toString()}`)
      .then(({ data }) => {
        setComments(data);
        setLastUpdated(new Date());
      })
      .catch(console.error)
      .finally(() => setLoadingComments(false));
  }, []);

  // Refetch immediately when server-side filters change
  useEffect(() => {
    fetchComments(true);
  }, [clientFilter, dateFrom, dateTo, fetchComments]);

  // Background poll — no spinner so table doesn't flash
  useEffect(() => {
    const id = setInterval(() => fetchComments(false), POLL_MS);
    return () => clearInterval(id);
  }, [fetchComments]);

  // Poll for liveness task result every 3 seconds while a task_id is set
  useEffect(() => {
    if (!livenessTaskId) return;
    livenessPollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${livenessTaskId}`);
        if (data.state === 'SUCCESS') {
          clearInterval(livenessPollerRef.current);
          setLivenessTaskId(null);
          setLivenessTaskResult(data.result);
          setLivenessTaskDone(true);
          fetchComments(true);
          onRefresh?.();
        } else if (data.state === 'FAILURE') {
          clearInterval(livenessPollerRef.current);
          setLivenessTaskId(null);
          setLivenessTaskResult({ error: data.error || 'Task failed.' });
          setLivenessTaskDone(true);
        }
      } catch {
        // network blip — keep polling
      }
    }, 3000);
    return () => clearInterval(livenessPollerRef.current);
  }, [livenessTaskId, fetchComments]);

  // Poll for backfill task result
  useEffect(() => {
    if (!backfillTaskId) return;
    backfillPollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${backfillTaskId}`);
        if (data.state === 'SUCCESS') {
          clearInterval(backfillPollerRef.current);
          setBackfillTaskId(null);
          setBackfillTaskResult(data.result);
          setBackfillTaskDone(true);
          fetchComments(true);
          onRefresh?.();
        } else if (data.state === 'FAILURE') {
          clearInterval(backfillPollerRef.current);
          setBackfillTaskId(null);
          setBackfillTaskResult({ error: data.error || 'Task failed.' });
          setBackfillTaskDone(true);
        }
      } catch { /* network blip */ }
    }, 3000);
    return () => clearInterval(backfillPollerRef.current);
  }, [backfillTaskId, fetchComments]);

  // Poll for submitted-at backfill task result
  useEffect(() => {
    if (!submittedAtBackfillTaskId) return;
    submittedAtBackfillPollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${submittedAtBackfillTaskId}`);
        if (data.status === 'SUCCESS') {
          clearInterval(submittedAtBackfillPollerRef.current);
          setSubmittedAtBackfillTaskId(null);
          setSubmittedAtBackfillTaskResult(data.result);
          setSubmittedAtBackfillTaskDone(true);
          fetchComments();
        } else if (data.status === 'FAILURE') {
          clearInterval(submittedAtBackfillPollerRef.current);
          setSubmittedAtBackfillTaskId(null);
          setSubmittedAtBackfillTaskResult({ error: data.error || 'Task failed.' });
          setSubmittedAtBackfillTaskDone(true);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(submittedAtBackfillPollerRef.current);
  }, [submittedAtBackfillTaskId, fetchComments]);

  // Poll for recheck-accounts task result
  useEffect(() => {
    if (!recheckTaskId) return;
    recheckPollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${recheckTaskId}`);
        if (data.state === 'SUCCESS') {
          clearInterval(recheckPollerRef.current);
          setRecheckTaskId(null);
          setRecheckTaskResult(data.result);
          setRecheckTaskDone(true);
        } else if (data.state === 'FAILURE') {
          clearInterval(recheckPollerRef.current);
          setRecheckTaskId(null);
          setRecheckTaskResult({ error: data.error || 'Task failed.' });
          setRecheckTaskDone(true);
        }
      } catch { /* network blip */ }
    }, 3000);
    return () => clearInterval(recheckPollerRef.current);
  }, [recheckTaskId]);

  // Poll for check-banned-accounts task result
  useEffect(() => {
    if (!bannedCheckTaskId) return;
    bannedCheckPollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${bannedCheckTaskId}`);
        if (data.state === 'SUCCESS') {
          clearInterval(bannedCheckPollerRef.current);
          setBannedCheckTaskId(null);
          setBannedCheckTaskResult(data.result);
          setBannedCheckTaskDone(true);
          fetchComments?.(true);
          onRefresh?.();
        } else if (data.state === 'FAILURE') {
          clearInterval(bannedCheckPollerRef.current);
          setBannedCheckTaskId(null);
          setBannedCheckTaskResult({ error: data.error || 'Task failed.' });
          setBannedCheckTaskDone(true);
        }
      } catch { /* network blip */ }
    }, 3000);
    return () => clearInterval(bannedCheckPollerRef.current);
  }, [bannedCheckTaskId]);

  // Poll for check-niche task result
  useEffect(() => {
    if (!checkNicheTaskId) return;
    checkNichePollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${checkNicheTaskId}`);
        if (data.state === 'SUCCESS') {
          clearInterval(checkNichePollerRef.current);
          setCheckNicheTaskId(null);
          setCheckNicheTaskResult(data.result);
          setCheckNicheTaskDone(true);
        } else if (data.state === 'FAILURE') {
          clearInterval(checkNichePollerRef.current);
          setCheckNicheTaskId(null);
          setCheckNicheTaskResult({ error: data.error || 'Task failed.' });
          setCheckNicheTaskDone(true);
        }
      } catch { /* network blip */ }
    }, 3000);
    return () => clearInterval(checkNichePollerRef.current);
  }, [checkNicheTaskId]);

  // Poll for sync-nocodb-clients task result
  useEffect(() => {
    if (!syncNocodbTaskId) return;
    syncNocodbPollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${syncNocodbTaskId}`);
        if (data.state === 'SUCCESS') {
          clearInterval(syncNocodbPollerRef.current);
          setSyncNocodbTaskId(null);
          setSyncNocodbTaskResult(data.result);
          setSyncNocodbTaskDone(true);
          onRefresh?.();
        } else if (data.state === 'FAILURE') {
          clearInterval(syncNocodbPollerRef.current);
          setSyncNocodbTaskId(null);
          setSyncNocodbTaskResult({ error: data.error || 'Task failed.' });
          setSyncNocodbTaskDone(true);
        }
      } catch { /* network blip */ }
    }, 3000);
    return () => clearInterval(syncNocodbPollerRef.current);
  }, [syncNocodbTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for archive check task result
  useEffect(() => {
    if (!archiveCheckTaskId) return;
    archiveCheckPollerRef.current = setInterval(async () => {
      try {
        const { data } = await redditApi.get(`/admin/dev/task-result/?task_id=${archiveCheckTaskId}`);
        if (data.state === 'SUCCESS') {
          clearInterval(archiveCheckPollerRef.current);
          setArchiveCheckTaskId(null);
          setArchiveCheckTaskResult(data.result);
          setArchiveCheckTaskDone(true);
          fetchComments(true);
          onRefresh?.();
        } else if (data.state === 'FAILURE') {
          clearInterval(archiveCheckPollerRef.current);
          setArchiveCheckTaskId(null);
          setArchiveCheckTaskResult({ error: data.error || 'Task failed.' });
          setArchiveCheckTaskDone(true);
        }
      } catch { /* network blip */ }
    }, 3000);
    return () => clearInterval(archiveCheckPollerRef.current);
  }, [archiveCheckTaskId, fetchComments]);

  const ov = stats?.overview || {};
  const redditors = stats?.redditors || [];

  const lastUpdatedStr = lastUpdated
    ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <>
      {showClearConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowClearConfirm(false)}>
          <div className="rh-modal" style={{ maxWidth: 400, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#dc2626' }}>Clear — {selectedClient}</h3>
              <button className="rh-modal-close" onClick={() => setShowClearConfirm(false)}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6"/>
                    <path d="M9 6V4h6v2"/>
                  </svg>
                </div>
                <div>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 6 }}>
                    Delete all comments & assignments for <span style={{ color: '#dc2626' }}>{selectedClient}</span>?
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.5 }}>
                    This will permanently remove every scheduled, submitted, and deleted comment for this client, along with their assignment record. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowClearConfirm(false)}>Cancel</button>
              <button
                className="rh-btn"
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                onClick={confirmClearAll}
              >
                Yes, delete all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Liveness Check — confirm modal ── */}
      {showLivenessConfirm && (() => {
        const uniqueClientNames = livenessClientList;
        const disabled = livenessDispatching || livenessStatuses.size === 0;
        const STATUS_OPTS = [
          { key: 'submitted', label: 'Submitted', bg: '#fef3c7', color: '#92400e', count: livenessPreview?.submitted ?? '…' },
          { key: 'live',      label: 'Live',      bg: 'var(--rh-green-bg)', color: 'var(--rh-green)', count: livenessPreview?.live ?? '…' },
          { key: 'deleted',   label: 'Deleted',   bg: 'var(--rh-red-bg)', color: 'var(--rh-red)', count: livenessPreview?.deleted ?? '…' },
        ];
        return (
          <div className="rh-modal-overlay" onClick={(e) => { if (!livenessDispatching && e.target === e.currentTarget) setShowLivenessConfirm(false); }}>
            <div className="rh-modal" style={{ maxWidth: 460, width: '95%' }}>
              <div className="rh-modal-header">
                <h3 className="rh-modal-title" style={{ color: '#1d4ed8' }}>Run Liveness Check</h3>
                <button className="rh-modal-close" onClick={() => setShowLivenessConfirm(false)} disabled={livenessDispatching}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="rh-modal-body" style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>

                <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, margin: 0 }}>
                  Hits the Reddit API for each selected comment and updates their status to <strong>Live</strong> or <strong>Deleted</strong>. Runs in the background.
                </p>

                {/* Status checkboxes */}
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                    Check statuses
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {STATUS_OPTS.map(({ key, label, bg, color, count }) => {
                      const checked = livenessStatuses.has(key);
                      return (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: livenessDispatching ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                          <div
                            onClick={() => !livenessDispatching && toggleLivenessStatus(key)}
                            style={{
                              width: 18, height: 18, borderRadius: 4, flexShrink: 0, border: '2px solid',
                              borderColor: checked ? '#1d4ed8' : 'var(--rh-border)',
                              background: checked ? '#1d4ed8' : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s', cursor: livenessDispatching ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {checked && (
                              <svg width="10" height="10" fill="none" stroke="#fff" strokeWidth="2.8" viewBox="0 0 24 24">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            )}
                          </div>
                          <span style={{ fontSize: 13, color: 'var(--rh-text)', flex: 1 }}>{label}</span>
                          {livenessPreview && !livenessPreview.error && (
                            <span style={{ background: bg, color, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                              {count}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {livenessStatuses.size === 0 && (
                    <p style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>Select at least one status.</p>
                  )}
                </div>

                {/* Client filter */}
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    Client
                  </p>
                  <select
                    value={livenessClient}
                    onChange={(e) => setLivenessClientAndRefresh(e.target.value)}
                    disabled={livenessDispatching}
                    style={{
                      width: '100%', border: '1.5px solid var(--rh-border)', borderRadius: 8,
                      padding: '8px 12px', fontSize: 13, color: 'var(--rh-text)',
                      background: '#fff', cursor: 'pointer', outline: 'none',
                    }}
                  >
                    <option value="">All Clients</option>
                    {uniqueClientNames.map((cn) => (
                      <option key={cn} value={cn}>{cn}</option>
                    ))}
                  </select>
                </div>

                {/* Total preview */}
                {livenessPreview && !livenessPreview.error && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: 'var(--rh-blue-bg)', color: 'var(--rh-blue)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                      {livenessPreview.total} comments to check
                    </span>
                    {livenessClient && (
                      <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>for {livenessClient}</span>
                    )}
                  </div>
                )}
                {livenessPreview?.error && (
                  <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{livenessPreview.error}</p>
                )}
                {!livenessPreview && (
                  <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', margin: 0 }}>Loading preview…</p>
                )}
              </div>

              <div className="rh-modal-footer">
                <button className="rh-btn rh-btn-secondary" onClick={() => setShowLivenessConfirm(false)} disabled={livenessDispatching}>
                  Cancel
                </button>
                <button
                  className="rh-btn"
                  style={{
                    background: disabled || livenessPreview?.error ? '#93c5fd' : '#1d4ed8',
                    color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                    fontWeight: 600, fontSize: 13,
                    cursor: disabled || livenessPreview?.error ? 'not-allowed' : 'pointer',
                  }}
                  onClick={confirmLivenessCheck}
                  disabled={disabled || !!livenessPreview?.error}
                >
                  {livenessDispatching ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      Dispatching…
                    </span>
                  ) : 'Run in Background'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Liveness Check — floating "running" banner ── */}
      {livenessTaskId && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(29,78,216,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(29,78,216,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>Liveness check running…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>You'll be notified when it's done</div>
          </div>
        </div>
      )}

      {/* ── Liveness Check — result popup ── */}
      {livenessTaskDone && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: livenessTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(29,78,216,0.25)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 280, maxWidth: 340,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {livenessTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: livenessTaskResult?.error ? '#dc2626' : '#1d4ed8' }}>
                {livenessTaskResult?.error ? 'Check failed' : 'Check complete'}
              </span>
            </div>
            <button
              onClick={() => { setLivenessTaskDone(false); setLivenessTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {livenessTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{livenessTaskResult.error}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Checked', value: livenessTaskResult?.checked ?? 0, color: '#1d4ed8' },
                { label: '→ Live', value: livenessTaskResult?.marked_live ?? 0, color: '#16a34a' },
                ...(livenessTaskResult?.restored > 0 ? [{ label: '↑ Restored', value: livenessTaskResult.restored, color: '#7c3aed' }] : []),
                { label: '→ Deleted', value: livenessTaskResult?.marked_deleted ?? 0, color: '#dc2626' },
                { label: 'Skipped', value: livenessTaskResult?.skipped ?? 0, color: '#6b7280' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Backfill Fields — confirm modal ── */}
      {showBackfillConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!backfillDispatching && e.target === e.currentTarget) setShowBackfillConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 440, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#6d28d9' }}>Fix Data</h3>
              <button className="rh-modal-close" onClick={() => setShowBackfillConfirm(false)} disabled={backfillDispatching}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#6d28d9" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="1 4 1 10 7 10"/>
                    <path d="M3.51 15a9 9 0 1 0 .49-3"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    Normalise URLs, backfill subreddits & fill post dates?
                  </p>
                  {!backfillPreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : backfillPreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{backfillPreview.error}</p>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
                        Fixes three data quality issues: adds <code>https://www.reddit.com</code> prefix to any bare URLs, populates the empty subreddit field from the URL, and fetches missing post dates from Reddit. Runs in the background.
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ background: '#f5f3ff', color: '#6d28d9', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {backfillPreview.url_needs_fix} URLs to fix
                        </span>
                        <span style={{ background: '#f5f3ff', color: '#6d28d9', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {backfillPreview.sub_needs_fix} subreddits to fill
                        </span>
                        <span style={{ background: '#f5f3ff', color: '#6d28d9', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {backfillPreview.date_needs_fix} post dates to fill
                        </span>
                        <span style={{ background: 'var(--rh-blue-bg)', color: 'var(--rh-blue)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {backfillPreview.total} total records
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowBackfillConfirm(false)} disabled={backfillDispatching}>Cancel</button>
              <button
                className="rh-btn"
                style={{
                  background: backfillDispatching || !backfillPreview || backfillPreview.error ? '#c4b5fd' : '#6d28d9',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: backfillDispatching || !backfillPreview || backfillPreview.error ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmBackfill}
                disabled={backfillDispatching || !backfillPreview || !!backfillPreview.error}
              >
                {backfillDispatching ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dispatching…
                  </span>
                ) : 'Run in Background'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Backfill — floating running banner ── */}
      {backfillTaskId && (
        <div style={{
          position: 'fixed', bottom: livenessTaskId ? 100 : 24, right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(109,40,217,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(109,40,217,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#6d28d9' }}>Fixing data in background…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>You'll be notified when it's done</div>
          </div>
        </div>
      )}

      {/* ── Backfill — result popup ── */}
      {backfillTaskDone && (
        <div style={{
          position: 'fixed', bottom: livenessTaskDone ? 220 : 24, right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: backfillTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(109,40,217,0.25)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 260, maxWidth: 320,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {backfillTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: backfillTaskResult?.error ? '#dc2626' : '#6d28d9' }}>
                {backfillTaskResult?.error ? 'Fix failed' : 'Fix complete'}
              </span>
            </div>
            <button
              onClick={() => { setBackfillTaskDone(false); setBackfillTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {backfillTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{backfillTaskResult.error}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'URLs fixed', value: backfillTaskResult?.url_fixed ?? 0, color: '#6d28d9' },
                { label: 'Subreddits filled', value: backfillTaskResult?.sub_fixed ?? 0, color: '#6d28d9' },
                { label: 'Dates filled', value: backfillTaskResult?.date_fixed ?? 0, color: '#6d28d9' },
                { label: 'Total records', value: backfillTaskResult?.total_checked ?? 0, color: '#6b7280' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Backfill Submitted At — confirm modal ── */}
      {showSubmittedAtBackfillConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!submittedAtBackfillDispatching && e.target === e.currentTarget) setShowSubmittedAtBackfillConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 460, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#b45309' }}>Backfill Submitted At</h3>
              <button className="rh-modal-close" onClick={() => setShowSubmittedAtBackfillConfirm(false)} disabled={submittedAtBackfillDispatching}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#b45309" strokeWidth="2.2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    Backfill missing Submitted at from scheduled publish dates?
                  </p>
                  {!submittedAtBackfillPreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : submittedAtBackfillPreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{submittedAtBackfillPreview.error}</p>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
                        Sets <code>submitted_at</code> from <code>publishing_date</code> (start of day) for posted comments that are missing it, and marks them approximate (*). Never overwrites real timestamps. Also adds a DWS deletion check when needed.
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ background: '#fffbeb', color: '#b45309', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {submittedAtBackfillPreview.eligible_count} to stamp
                        </span>
                        {Object.entries(submittedAtBackfillPreview.by_status || {}).map(([status, count]) => (
                          <span key={status} style={{ background: 'var(--rh-blue-bg)', color: 'var(--rh-blue)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                            {count} {status}
                          </span>
                        ))}
                        {(submittedAtBackfillPreview.dws_need_deletion_check || 0) > 0 && (
                          <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                            {submittedAtBackfillPreview.dws_need_deletion_check} DWS need deletion check
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowSubmittedAtBackfillConfirm(false)} disabled={submittedAtBackfillDispatching}>Cancel</button>
              <button
                className="rh-btn"
                style={{
                  background: submittedAtBackfillDispatching || !submittedAtBackfillPreview || submittedAtBackfillPreview.error ? '#fcd34d' : '#b45309',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: submittedAtBackfillDispatching || !submittedAtBackfillPreview || submittedAtBackfillPreview.error ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmSubmittedAtBackfill}
                disabled={submittedAtBackfillDispatching || !submittedAtBackfillPreview || !!submittedAtBackfillPreview.error}
              >
                {submittedAtBackfillDispatching ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dispatching…
                  </span>
                ) : 'Run in Background'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Backfill Submitted At — floating running banner ── */}
      {submittedAtBackfillTaskId && (
        <div style={{
          position: 'fixed', bottom: (livenessTaskId ? 76 : 0) + (backfillTaskId ? 76 : 0) + 24, right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(180,83,9,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(180,83,9,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309' }}>Backfilling Submitted at…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>You'll be notified when it's done</div>
          </div>
        </div>
      )}

      {/* ── Backfill Submitted At — result popup ── */}
      {submittedAtBackfillTaskDone && (
        <div style={{
          position: 'fixed',
          bottom: (livenessTaskDone ? 220 : 0) + (backfillTaskDone ? 220 : 0) + 24,
          right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: submittedAtBackfillTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(180,83,9,0.25)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 260, maxWidth: 320,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {submittedAtBackfillTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: submittedAtBackfillTaskResult?.error ? '#dc2626' : '#b45309' }}>
                {submittedAtBackfillTaskResult?.error ? 'Backfill failed' : 'Submitted at backfill complete'}
              </span>
            </div>
            <button
              onClick={() => { setSubmittedAtBackfillTaskDone(false); setSubmittedAtBackfillTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {submittedAtBackfillTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{submittedAtBackfillTaskResult.error}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Stamped approximate', value: submittedAtBackfillTaskResult?.stamped ?? 0, color: '#b45309' },
                { label: 'DWS checks added', value: submittedAtBackfillTaskResult?.dws_checks_created ?? 0, color: '#b45309' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Recheck Accounts — confirm modal ── */}
      {showRecheckConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!recheckDispatching && e.target === e.currentTarget) setShowRecheckConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 460, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#0f766e' }}>Recheck Accounts</h3>
              <button className="rh-modal-close" onClick={() => setShowRecheckConfirm(false)} disabled={recheckDispatching}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#f0fdfa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#0f766e" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4"/>
                    <path d="M21 12a9 9 0 1 1-2.64-6.36"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    Re-run the quality check on all existing accounts?
                  </p>
                  {!recheckPreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : recheckPreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{recheckPreview.error}</p>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
                        Each account is re-validated against the quality gate. Accounts that <strong>fail</strong> are blocked from future submissions and hidden from the submit dropdown — they are <strong>never deleted</strong>. Calls Reddit + AI per account, so this can take a while. Runs in the background.
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ background: '#f0fdfa', color: '#0f766e', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {recheckPreview.to_check} accounts to check
                        </span>
                        <span style={{ background: 'var(--rh-red-bg)', color: 'var(--rh-red)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {recheckPreview.currently_failed} currently blocked
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowRecheckConfirm(false)} disabled={recheckDispatching}>Cancel</button>
              <button
                className="rh-btn"
                style={{
                  background: recheckDispatching || !recheckPreview || recheckPreview.error || recheckPreview.to_check === 0 ? '#99f6e4' : '#0f766e',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: recheckDispatching || !recheckPreview || recheckPreview.error || recheckPreview.to_check === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmRecheck}
                disabled={recheckDispatching || !recheckPreview || !!recheckPreview.error || recheckPreview.to_check === 0}
              >
                {recheckDispatching ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dispatching…
                  </span>
                ) : 'Run in Background'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Recheck Accounts — floating running banner ── */}
      {recheckTaskId && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(15,118,110,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(15,118,110,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f766e" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f766e' }}>Re-checking accounts in background…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>This can take a few minutes</div>
          </div>
        </div>
      )}

      {/* ── Recheck Accounts — result popup ── */}
      {recheckTaskDone && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: recheckTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(15,118,110,0.25)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 260, maxWidth: 340,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {recheckTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: recheckTaskResult?.error ? '#dc2626' : '#0f766e' }}>
                {recheckTaskResult?.error ? 'Re-check failed' : 'Re-check complete'}
              </span>
            </div>
            <button
              onClick={() => { setRecheckTaskDone(false); setRecheckTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {recheckTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{recheckTaskResult.error}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Passed', value: recheckTaskResult?.passed ?? 0, color: '#16a34a' },
                { label: 'Newly blocked', value: recheckTaskResult?.newly_blocked ?? 0, color: '#dc2626' },
                { label: 'Failed (total)', value: recheckTaskResult?.failed ?? 0, color: '#dc2626' },
                { label: 'Needs review', value: recheckTaskResult?.needs_manual_review ?? 0, color: '#d97706' },
                { label: 'Errors', value: recheckTaskResult?.errors ?? 0, color: '#6b7280' },
                { label: 'Total checked', value: recheckTaskResult?.total_checked ?? 0, color: '#6b7280' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Check Banned Accounts — confirm modal ── */}
      {showBannedCheckConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!bannedCheckDispatching && e.target === e.currentTarget) setShowBannedCheckConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 480, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#9f1239' }}>Check Banned Accounts</h3>
              <button className="rh-modal-close" onClick={() => setShowBannedCheckConfirm(false)} disabled={bannedCheckDispatching}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#9f1239" strokeWidth="2.2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    Probe all in-use Reddit accounts for bans, then recheck their Live/Submitted comments?
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
                    Checks every usable account (passed or force-added; not failed, banned, or archived).
                    Matches the daily 06:30 UTC job. Newly banned accounts are marked Banned + Archived.
                    Their Live/Submitted comments are rechecked; unavailable ones are marked Deleted.
                    Emails and out-of-accounts reassignment run as usual.
                  </p>
                  {!bannedCheckPreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : bannedCheckPreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{bannedCheckPreview.error}</p>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ background: '#fff1f2', color: '#9f1239', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                        {bannedCheckPreview.to_check} usable account{bannedCheckPreview.to_check !== 1 ? 's' : ''} to check
                      </span>
                      <span style={{ background: 'var(--rh-green-bg)', color: 'var(--rh-green)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                        {bannedCheckPreview.live_comments} live comments
                      </span>
                      <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                        {bannedCheckPreview.submitted_comments} submitted
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowBannedCheckConfirm(false)} disabled={bannedCheckDispatching}>Cancel</button>
              <button
                className="rh-btn"
                style={{
                  background: bannedCheckDispatching || !bannedCheckPreview || bannedCheckPreview.error || bannedCheckPreview.to_check === 0 ? '#fecdd3' : '#9f1239',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: bannedCheckDispatching || !bannedCheckPreview || bannedCheckPreview.error || bannedCheckPreview.to_check === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmBannedCheck}
                disabled={bannedCheckDispatching || !bannedCheckPreview || !!bannedCheckPreview.error || bannedCheckPreview.to_check === 0}
              >
                {bannedCheckDispatching ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dispatching…
                  </span>
                ) : 'Run in Background'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Check Banned Accounts — floating running banner ── */}
      {bannedCheckTaskId && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(159,18,57,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(159,18,57,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9f1239" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#9f1239' }}>Checking banned accounts…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>Probing Reddit, then rechecking comments</div>
          </div>
        </div>
      )}

      {/* ── Check Banned Accounts — result popup ── */}
      {bannedCheckTaskDone && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: bannedCheckTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(159,18,57,0.25)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 260, maxWidth: 360,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {bannedCheckTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: bannedCheckTaskResult?.error ? '#dc2626' : '#9f1239' }}>
                {bannedCheckTaskResult?.error ? 'Banned check failed' : 'Banned check complete'}
              </span>
            </div>
            <button
              onClick={() => { setBannedCheckTaskDone(false); setBannedCheckTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {bannedCheckTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{bannedCheckTaskResult.error}</p>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Accounts checked', value: bannedCheckTaskResult?.checked ?? 0, color: '#6b7280' },
                  { label: 'Newly banned', value: bannedCheckTaskResult?.banned ?? 0, color: '#9f1239' },
                  { label: 'Comments → Deleted', value: bannedCheckTaskResult?.comments_marked_deleted ?? 0, color: '#dc2626' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                  </div>
                ))}
              </div>
              {!!bannedCheckTaskResult?.banned_usernames?.length && (
                <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
                  Banned: {bannedCheckTaskResult.banned_usernames.map((u) => `u/${u}`).join(', ')}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Sync Client Data (NocoDB) — confirm modal ── */}
      {showSyncNocodbConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!syncNocodbDispatching && e.target === e.currentTarget) setShowSyncNocodbConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 460, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#4338ca' }}>Sync Client Data</h3>
              <button className="rh-modal-close" onClick={() => setShowSyncNocodbConfirm(false)} disabled={syncNocodbDispatching}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#4338ca" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    Pull the latest 1-Sentence Description and Core Offering for every client from NocoDB?
                  </p>
                  {!syncNocodbPreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : syncNocodbPreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{syncNocodbPreview.error}</p>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
                        Fetches each client's 1-Sentence Description, Core Offering + website from NocoDB and
                        AI-extracts target niches, embeds them for niche-to-niche matching, then
                        recomputes every account's Gemini-judged client fit. Runs in the background.
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ background: '#eef2ff', color: '#4338ca', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {syncNocodbPreview.to_sync} client{syncNocodbPreview.to_sync !== 1 ? 's' : ''} to sync
                        </span>
                        <span style={{ background: 'var(--rh-border)', color: 'var(--rh-text-muted)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {syncNocodbPreview.already_synced} already synced
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowSyncNocodbConfirm(false)} disabled={syncNocodbDispatching}>Cancel</button>
              <button
                className="rh-btn"
                style={{
                  background: syncNocodbDispatching || !syncNocodbPreview || syncNocodbPreview.error || syncNocodbPreview.to_sync === 0 ? '#c7d2fe' : '#4338ca',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: syncNocodbDispatching || !syncNocodbPreview || syncNocodbPreview.error || syncNocodbPreview.to_sync === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmSyncNocodb}
                disabled={syncNocodbDispatching || !syncNocodbPreview || !!syncNocodbPreview.error || syncNocodbPreview.to_sync === 0}
              >
                {syncNocodbDispatching ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dispatching…
                  </span>
                ) : 'Run in Background'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sync Client Data — floating running banner ── */}
      {syncNocodbTaskId && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(67,56,202,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(67,56,202,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4338ca" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#4338ca' }}>Syncing client data in background…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>Pulling NocoDB data, then recomputing account fit</div>
          </div>
        </div>
      )}

      {/* ── Sync Client Data — result popup ── */}
      {syncNocodbTaskDone && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: syncNocodbTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(67,56,202,0.25)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 260, maxWidth: 340,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {syncNocodbTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#4338ca" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: syncNocodbTaskResult?.error ? '#dc2626' : '#4338ca' }}>
                {syncNocodbTaskResult?.error ? 'Client sync failed' : 'Client sync complete'}
              </span>
            </div>
            <button
              onClick={() => { setSyncNocodbTaskDone(false); setSyncNocodbTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {syncNocodbTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{syncNocodbTaskResult.error}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Total clients', value: syncNocodbTaskResult?.total_clients ?? 0, color: '#4338ca' },
                { label: 'Synced', value: syncNocodbTaskResult?.synced ?? 0, color: '#16a34a' },
                { label: 'Not found', value: syncNocodbTaskResult?.not_found ?? 0, color: '#d97706' },
                { label: 'Errors', value: syncNocodbTaskResult?.errors ?? 0, color: '#6b7280' },
                { label: 'Accounts recomputed', value: syncNocodbTaskResult?.accounts_recomputed ?? 0, color: '#4338ca' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Check Niche — confirm modal ── */}
      {showCheckNicheConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!checkNicheDispatching && e.target === e.currentTarget) setShowCheckNicheConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 460, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#be185d' }}>Check Niche</h3>
              <button className="rh-modal-close" onClick={() => setShowCheckNicheConfirm(false)} disabled={checkNicheDispatching}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#fdf2f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#be185d" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path d="M20.59 13.41L11 3.83V3H4v7h.83l9.58 9.59a2 2 0 0 0 2.83 0l3.35-3.35a2 2 0 0 0 0-2.83z"/>
                    <circle cx="7.5" cy="7.5" r="1.5" fill="#be185d" stroke="none"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    Re-classify niches and client fit for every existing account?
                  </p>
                  {!checkNichePreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : checkNichePreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{checkNichePreview.error}</p>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
                        Re-runs the full quality gate for every account, one at a time, which
                        refreshes each account's niches and Gemini-judged niche-to-niche fit against
                        every current client. Calls Reddit + AI per account, so this can take a while.
                        Runs in the background.
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ background: '#fdf2f8', color: '#be185d', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {checkNichePreview.to_check} accounts to check
                        </span>
                        <span style={{ background: 'var(--rh-border)', color: 'var(--rh-text-muted)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {checkNichePreview.with_niches} already have niches
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowCheckNicheConfirm(false)} disabled={checkNicheDispatching}>Cancel</button>
              <button
                className="rh-btn"
                style={{
                  background: checkNicheDispatching || !checkNichePreview || checkNichePreview.error || checkNichePreview.to_check === 0 ? '#fbcfe8' : '#be185d',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: checkNicheDispatching || !checkNichePreview || checkNichePreview.error || checkNichePreview.to_check === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmCheckNiche}
                disabled={checkNicheDispatching || !checkNichePreview || !!checkNichePreview.error || checkNichePreview.to_check === 0}
              >
                {checkNicheDispatching ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dispatching…
                  </span>
                ) : 'Run in Background'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Check Niche — floating running banner ── */}
      {checkNicheTaskId && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(190,24,93,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(190,24,93,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#be185d" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#be185d' }}>Checking niches in background…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>Processing accounts one by one — this can take a few minutes</div>
          </div>
        </div>
      )}

      {/* ── Check Niche — result popup ── */}
      {checkNicheTaskDone && (
        <div style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: checkNicheTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(190,24,93,0.25)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 260, maxWidth: 340,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {checkNicheTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#be185d" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: checkNicheTaskResult?.error ? '#dc2626' : '#be185d' }}>
                {checkNicheTaskResult?.error ? 'Niche check failed' : 'Niche check complete'}
              </span>
            </div>
            <button
              onClick={() => { setCheckNicheTaskDone(false); setCheckNicheTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {checkNicheTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{checkNicheTaskResult.error}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'With niches', value: checkNicheTaskResult?.with_niches ?? 0, color: '#be185d' },
                { label: 'Passed', value: checkNicheTaskResult?.passed ?? 0, color: '#16a34a' },
                { label: 'Failed', value: checkNicheTaskResult?.failed ?? 0, color: '#dc2626' },
                { label: 'Needs review', value: checkNicheTaskResult?.needs_manual_review ?? 0, color: '#d97706' },
                { label: 'Errors', value: checkNicheTaskResult?.errors ?? 0, color: '#6b7280' },
                { label: 'Total checked', value: checkNicheTaskResult?.total_checked ?? 0, color: '#6b7280' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Send Reminders — confirm modal ── */}
      {showSendRemindersConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!sendRemindersSending && e.target === e.currentTarget) setShowSendRemindersConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 520, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#7c3aed' }}>Send Reminders</h3>
              <button className="rh-modal-close" onClick={() => setShowSendRemindersConfirm(false)} disabled={sendRemindersSending}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#7c3aed" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3z"/>
                    <path d="M9 17v1a3 3 0 0 0 6 0v-1"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    Send all pending overdue-posting reminder emails now
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
                    Runs the same logic as the daily 09:00 UTC reminder task. Sends Day +1, +2, and +3 reminders for any overdue SCHEDULED comments that haven't been reminded yet. Safe to run at any time — already-sent reminders are never duplicated.
                  </p>

                  {!sendRemindersPreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : sendRemindersPreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{sendRemindersPreview.error}</p>
                  ) : sendRemindersPreview.total_emails === 0 ? (
                    <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#15803d', fontWeight: 500 }}>
                      ✓ No pending reminders — all overdue comments have already been reminded.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                        <span style={{ background: '#f5f3ff', color: '#6d28d9', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {sendRemindersPreview.total_emails} email{sendRemindersPreview.total_emails !== 1 ? 's' : ''} to send
                        </span>
                        <span style={{ background: '#f5f3ff', color: '#6d28d9', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {sendRemindersPreview.total_comments} overdue comment{sendRemindersPreview.total_comments !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div style={{ maxHeight: 220, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--rh-border)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: 'var(--rh-bg-secondary)' }}>
                              <th style={{ textAlign: 'left', padding: '7px 12px', color: 'var(--rh-text-muted)', fontWeight: 600 }}>Redditor</th>
                              <th style={{ textAlign: 'center', padding: '7px 8px', color: 'var(--rh-text-muted)', fontWeight: 600 }}>Day</th>
                              <th style={{ textAlign: 'center', padding: '7px 8px', color: 'var(--rh-text-muted)', fontWeight: 600 }}>Comments</th>
                              <th style={{ textAlign: 'left', padding: '7px 8px', color: 'var(--rh-text-muted)', fontWeight: 600 }}>Clients</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sendRemindersPreview.pending.map((row, i) => (
                              <tr key={i} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--rh-border)' }}>
                                <td style={{ padding: '7px 12px', color: 'var(--rh-text)', fontWeight: 500 }}>{row.redditor}</td>
                                <td style={{ textAlign: 'center', padding: '7px 8px' }}>
                                  <span style={{
                                    background: row.day_offset === 3 ? '#fef2f2' : row.day_offset === 2 ? '#fffbeb' : '#f5f3ff',
                                    color: row.day_offset === 3 ? '#dc2626' : row.day_offset === 2 ? '#d97706' : '#7c3aed',
                                    borderRadius: 5, padding: '2px 7px', fontWeight: 700, fontSize: 11,
                                  }}>
                                    +{row.day_offset}{row.day_offset === 3 ? ' CC' : ''}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'center', padding: '7px 8px', fontWeight: 600, color: 'var(--rh-text)' }}>{row.comment_count}</td>
                                <td style={{ padding: '7px 8px', color: 'var(--rh-text-muted)', fontSize: 11 }}>{row.clients.join(', ')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowSendRemindersConfirm(false)} disabled={sendRemindersSending}>
                Cancel
              </button>
              <button
                className="rh-btn"
                style={{
                  background: sendRemindersSending || !sendRemindersPreview || sendRemindersPreview.error || sendRemindersPreview.total_emails === 0
                    ? '#ddd6fe' : '#7c3aed',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: sendRemindersSending || !sendRemindersPreview || sendRemindersPreview.error || sendRemindersPreview.total_emails === 0
                    ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmSendReminders}
                disabled={sendRemindersSending || !sendRemindersPreview || !!sendRemindersPreview.error || sendRemindersPreview.total_emails === 0}
              >
                {sendRemindersSending ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Sending…
                  </span>
                ) : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Send Reminders — result toast ── */}
      {sendRemindersResult && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: sendRemindersResult.failed > 0 ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(124,58,237,0.3)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 260, maxWidth: 360,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {sendRemindersResult.failed > 0 ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#7c3aed" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontWeight: 700, fontSize: 14, color: sendRemindersResult.failed > 0 ? '#dc2626' : '#7c3aed' }}>
                {sendRemindersResult.failed > 0 ? 'Some reminders failed' : 'Reminders sent'}
              </span>
            </div>
            <button onClick={() => setSendRemindersResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rh-text-muted)', padding: 2 }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.5 }}>
            <span style={{ color: '#7c3aed', fontWeight: 600 }}>{sendRemindersResult.sent}</span> email{sendRemindersResult.sent !== 1 ? 's' : ''} sent
            {sendRemindersResult.failed > 0 && (
              <span>, <span style={{ color: '#dc2626', fontWeight: 600 }}>{sendRemindersResult.failed}</span> failed</span>
            )}
          </div>
          {sendRemindersResult.results?.filter(r => r.status === 'failed').map((r, i) => (
            <div key={i} style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
              ✗ {r.redditor} day+{r.day_offset}: {r.error}
            </div>
          ))}
        </div>
      )}

      {/* ── Reset Overdue — confirm modal ── */}
      {showResetOverdueConfirm && (
        <div className="rh-modal-overlay" onClick={() => { if (!resetOverdueRunning) { setShowResetOverdueConfirm(false); setResetOverdueResult(null); } }}>
          <div className="rh-modal" style={{ maxWidth: 440, padding: '28px 28px 24px' }} onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: 'rgba(180,83,9,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="18" height="18" fill="none" stroke="#b45309" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#b45309' }}>Reset Overdue Incidents</h3>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>{assignedRedditor || 'This redditor'}</p>
              </div>
            </div>

            <p style={{ fontSize: 13, color: 'var(--rh-text)', lineHeight: 1.6, marginBottom: 16 }}>
              This clears the <strong>Total Overdue Incidents</strong> history for this redditor — all reminder logs are deleted and overdue day counters are reset to zero. Comments that are currently overdue remain scheduled as-is.
            </p>

            {!resetOverdueResult && (
              <>
                {/* Preview block */}
                <div style={{
                  border: '1px solid rgba(180,83,9,0.2)', borderRadius: 10,
                  background: 'rgba(180,83,9,0.04)', padding: '14px 16px',
                  marginBottom: 20, display: 'flex', gap: 24,
                }}>
                  {resetOverduePreview === null ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : resetOverduePreview?.error ? (
                    <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>{resetOverduePreview.error}</p>
                  ) : (
                    <>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 26, fontWeight: 700, color: '#b45309', lineHeight: 1 }}>{resetOverduePreview.incident_count}</div>
                        <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>incidents</div>
                      </div>
                      <div style={{ width: 1, background: 'rgba(180,83,9,0.15)' }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--rh-text)', lineHeight: 1 }}>{resetOverduePreview.total_reminder_logs}</div>
                        <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>reminder logs</div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    style={{ background: 'none', border: '1px solid var(--rh-border)', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: 'var(--rh-text)' }}
                    onClick={() => setShowResetOverdueConfirm(false)}
                    disabled={resetOverdueRunning}
                  >
                    Cancel
                  </button>
                  <button
                    style={{ background: '#b45309', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: (resetOverdueRunning || !resetOverduePreview || !!resetOverduePreview?.error) ? 'not-allowed' : 'pointer', opacity: (resetOverdueRunning || !resetOverduePreview || !!resetOverduePreview?.error) ? 0.6 : 1 }}
                    onClick={confirmResetOverdue}
                    disabled={resetOverdueRunning || !resetOverduePreview || !!resetOverduePreview?.error}
                  >
                    {resetOverdueRunning ? 'Resetting…' : 'Reset Incidents'}
                  </button>
                </div>
              </>
            )}

            {resetOverdueResult && (
              <>
                <div style={{
                  border: `1px solid ${resetOverdueResult.ok ? '#bbf7d0' : '#fecaca'}`,
                  borderRadius: 10,
                  background: resetOverdueResult.ok ? '#f0fdf4' : '#fef2f2',
                  padding: '14px 16px', marginBottom: 20,
                }}>
                  {resetOverdueResult.ok ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <svg width="20" height="20" fill="none" stroke="#16a34a" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>Incidents cleared successfully</div>
                        <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>
                          {resetOverdueResult.cleared_incidents} reminder log{resetOverdueResult.cleared_incidents !== 1 ? 's' : ''} deleted
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#dc2626' }}>{resetOverdueResult.error}</div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    style={{ background: 'none', border: '1px solid var(--rh-border)', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: 'var(--rh-text)' }}
                    onClick={() => { setShowResetOverdueConfirm(false); setResetOverdueResult(null); }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {/* ── Fill Quota Shortfalls — confirm modal ── */}
      {showFillQuotaConfirm && (
        <div
          className="rh-modal-overlay"
          onClick={(e) => {
            if (!fillQuotaRunning && e.target === e.currentTarget) setShowFillQuotaConfirm(false);
          }}
        >
          <div className="rh-modal" style={{ maxWidth: 520, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#b91c1c' }}>Fill Quota Shortfalls</h3>
              <button
                className="rh-modal-close"
                onClick={() => setShowFillQuotaConfirm(false)}
                disabled={fillQuotaRunning}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 16 }}>
                Pulls <strong>future-month</strong> scheduled comments into past/current months that are
                short of monthly LIVE quota. Future months stay locked for redditors; current-month
                (and overdue) comments become available to post.
              </p>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                  Client
                </label>
                <select
                  value={fillQuotaClient}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFillQuotaClient(v);
                    setFillQuotaResult(null);
                    refreshFillQuotaPreview(v);
                  }}
                  disabled={fillQuotaRunning}
                  style={{
                    width: '100%', border: '1.5px solid var(--rh-border)', borderRadius: 8,
                    padding: '8px 12px', fontSize: 13, color: 'var(--rh-text)',
                    background: '#fff', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="">All clients ({fillQuotaClientList.length || fillQuotaPreview?.total_clients || '…'})</option>
                  {(fillQuotaClientList.length
                    ? fillQuotaClientList
                    : (fillQuotaPreview?.clients || []).map((c) => c.client_name)
                  ).map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {!fillQuotaPreview ? (
                <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
              ) : fillQuotaPreview.error ? (
                <p style={{ fontSize: 13, color: '#dc2626' }}>{fillQuotaPreview.error}</p>
              ) : (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                  padding: '10px 14px', fontSize: 13, color: '#991b1b', marginBottom: 8,
                }}>
                  Will rebalance{' '}
                  <strong>
                    {fillQuotaClient
                      ? `1 client (${fillQuotaClient})`
                      : `${fillQuotaPreview.total_clients} client${fillQuotaPreview.total_clients === 1 ? '' : 's'}`}
                  </strong>
                  {' '}with a monthly quota.
                </div>
              )}

              {fillQuotaResult && (
                <div style={{
                  marginTop: 14,
                  background: fillQuotaResult.error ? '#fef2f2' : '#f0fdf4',
                  border: `1px solid ${fillQuotaResult.error ? '#fecaca' : '#bbf7d0'}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  maxHeight: 240,
                  overflowY: 'auto',
                }}>
                  {fillQuotaResult.error ? (
                    <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>{fillQuotaResult.error}</p>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#15803d' }}>
                        Done — pulled {fillQuotaResult.total_pulled} comment{fillQuotaResult.total_pulled === 1 ? '' : 's'} across{' '}
                        {fillQuotaResult.clients_processed} client{fillQuotaResult.clients_processed === 1 ? '' : 's'}
                      </p>
                      {(fillQuotaResult.results || []).filter((r) => r.pulled > 0).length === 0 ? (
                        <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>
                          No shortfalls to fill (or no future scheduled inventory to pull).
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(fillQuotaResult.results || [])
                            .filter((r) => r.pulled > 0)
                            .map((r) => (
                              <div key={r.client} style={{ fontSize: 12, color: 'var(--rh-text)' }}>
                                <strong>{r.client}</strong>: pulled {r.pulled}
                                {r.per_month && Object.keys(r.per_month).length > 0 && (
                                  <span style={{ color: 'var(--rh-text-muted)' }}>
                                    {' '}({Object.entries(r.per_month).map(([m, n]) => `${m}: ${n}`).join(', ')})
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="rh-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 28px' }}>
              <button
                className="rh-btn rh-btn-secondary"
                onClick={() => setShowFillQuotaConfirm(false)}
                disabled={fillQuotaRunning}
              >
                {fillQuotaResult && !fillQuotaResult.error ? 'Close' : 'Cancel'}
              </button>
              {!(fillQuotaResult && !fillQuotaResult.error) && (
                <button
                  className="rh-btn"
                  onClick={confirmFillQuotaShortfalls}
                  disabled={
                    fillQuotaRunning
                    || !fillQuotaPreview
                    || !!fillQuotaPreview.error
                    || (fillQuotaPreview.total_clients === 0)
                  }
                  style={{
                    background: fillQuotaRunning ? '#fca5a5' : '#b91c1c',
                    color: '#fff',
                    border: 'none',
                    cursor: fillQuotaRunning ? 'not-allowed' : 'pointer',
                  }}
                >
                  {fillQuotaRunning ? 'Filling…' : 'Fill Shortfalls'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Pull Assignments — confirm modal ── */}
      {showPullAssignmentsConfirm && (
        <div
          className="rh-modal-overlay"
          onClick={(e) => {
            if (!pullAssignmentsRunning && e.target === e.currentTarget) setShowPullAssignmentsConfirm(false);
          }}
        >
          <div className="rh-modal" style={{ maxWidth: 360, width: '92%' }}>
            <div className="rh-modal-header" style={{ padding: '14px 18px' }}>
              <h3 className="rh-modal-title" style={{ fontSize: 15 }}>Pull Assignments</h3>
              <button
                className="rh-modal-close"
                onClick={() => setShowPullAssignmentsConfirm(false)}
                disabled={pullAssignmentsRunning}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '14px 18px' }}>
              <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--rh-text)' }}>
                {clientFilter}
              </p>

              {!pullAssignmentsPreview ? (
                <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--rh-text-muted)' }}>
                  Loading future scheduled…
                </p>
              ) : pullAssignmentsPreview.error ? (
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#dc2626' }}>
                  {pullAssignmentsPreview.error}
                </p>
              ) : (
                <div style={{
                  marginBottom: 12,
                  background: 'var(--rh-bg)',
                  border: '1px solid var(--rh-border)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 13,
                  color: 'var(--rh-text)',
                  lineHeight: 1.45,
                }}>
                  <strong>{pullAssignmentsPreview.future_available ?? 0}</strong>
                  {' '}future-month scheduled comment
                  {(pullAssignmentsPreview.future_available ?? 0) === 1 ? '' : 's'} available
                </div>
              )}

              {!(pullAssignmentsResult && !pullAssignmentsResult.error) && (
                <div>
                  <label
                    htmlFor="pull-assignments-count"
                    style={{
                      fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)',
                      textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6,
                    }}
                  >
                    How many to pull into this month
                  </label>
                  <input
                    id="pull-assignments-count"
                    type="number"
                    min={1}
                    max={Math.min(200, pullAssignmentsPreview?.future_available || 200)}
                    step={1}
                    value={pullAssignmentsCount}
                    disabled={
                      pullAssignmentsRunning
                      || !pullAssignmentsPreview
                      || !!pullAssignmentsPreview?.error
                      || (pullAssignmentsPreview?.future_available ?? 0) === 0
                    }
                    onChange={(e) => {
                      setPullAssignmentsResult(null);
                      setPullAssignmentsCount(e.target.value);
                    }}
                    style={{
                      width: 96, border: '1.5px solid var(--rh-border)', borderRadius: 8,
                      padding: '7px 10px', fontSize: 13, color: 'var(--rh-text)',
                      background: '#fff', outline: 'none',
                    }}
                  />
                </div>
              )}

              {pullAssignmentsResult && (
                <div style={{
                  marginTop: 12,
                  background: pullAssignmentsResult.error ? '#fef2f2' : '#f0fdf4',
                  border: `1px solid ${pullAssignmentsResult.error ? '#fecaca' : '#bbf7d0'}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                }}>
                  {pullAssignmentsResult.error ? (
                    <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>{pullAssignmentsResult.error}</p>
                  ) : (
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#15803d' }}>
                      {pullAssignmentsResult.pulled === 0
                        ? 'No future scheduled comments available to pull.'
                        : `Pulled ${pullAssignmentsResult.pulled} of ${pullAssignmentsResult.requested} into ${pullAssignmentsResult.target_month || 'this month'}.`}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="rh-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px' }}>
              <button
                className="rh-btn-secondary"
                onClick={() => setShowPullAssignmentsConfirm(false)}
                disabled={pullAssignmentsRunning}
              >
                {pullAssignmentsResult && !pullAssignmentsResult.error ? 'Close' : 'Cancel'}
              </button>
              {!(pullAssignmentsResult && !pullAssignmentsResult.error) && (
                <button
                  className="rh-btn-primary"
                  onClick={confirmPullAssignments}
                  disabled={
                    pullAssignmentsRunning
                    || !pullAssignmentsPreview
                    || !!pullAssignmentsPreview?.error
                    || (pullAssignmentsPreview?.future_available ?? 0) === 0
                  }
                  style={{ padding: '9px 16px', fontSize: 13 }}
                >
                  {pullAssignmentsRunning ? 'Pulling…' : 'Pull'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Archive Check — confirm modal ── */}
      {showArchiveCheckConfirm && (
        <div className="rh-modal-overlay" onClick={(e) => { if (!archiveCheckDispatching && e.target === e.currentTarget) setShowArchiveCheckConfirm(false); }}>
          <div className="rh-modal" style={{ maxWidth: 480, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#d97706' }}>Archive Check</h3>
              <button className="rh-modal-close" onClick={() => setShowArchiveCheckConfirm(false)} disabled={archiveCheckDispatching}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#d97706" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 8 }}>
                    {archiveCheckMode === 'past'
                      ? 'Check overdue comments for locked or archived posts'
                      : 'Check upcoming scheduled comments for locked or archived posts'}
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
                    {archiveCheckMode === 'past'
                      ? <>Hits the Reddit API for each unique post URL linked to <strong>Overdue</strong> comments (missed their comment date). Any post that is <strong>locked</strong> or <strong>archived</strong> will have its comment marked <strong style={{ color: '#d97706' }}>Archived</strong> and the next queued comment for the same client will be shuffled up. Deleted, Live, and Instant Deletion statuses are excluded.</>
                      : <>Hits the Reddit API for each unique post URL in the queue. Any post that is <strong>locked</strong> or <strong>archived</strong> will have its comment marked <strong style={{ color: '#d97706' }}>Archived</strong> and the next queued comment for the same client will be shuffled up to fill the slot.</>
                    }
                  </p>

                  {/* Mode + Days options */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
                    {/* Mode toggle */}
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--rh-text-muted)', display: 'block', marginBottom: 4 }}>Direction</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {[['future', 'Future'], ['past', 'Past']].map(([m, label]) => (
                          <button
                            key={m}
                            onClick={() => {
                              setArchiveCheckMode(m);
                              refreshArchiveCheckPreview(archiveCheckDays, archiveCheckClient, m);
                            }}
                            style={{
                              padding: '5px 12px', fontSize: 12, borderRadius: 6, border: '1px solid',
                              borderColor: archiveCheckMode === m ? '#d97706' : 'var(--rh-border)',
                              background: archiveCheckMode === m ? '#fffbeb' : 'transparent',
                              color: archiveCheckMode === m ? '#d97706' : 'var(--rh-text-muted)',
                              cursor: 'pointer', fontWeight: archiveCheckMode === m ? 700 : 400,
                            }}
                            disabled={archiveCheckDispatching}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Days selector */}
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--rh-text-muted)', display: 'block', marginBottom: 4 }}>
                        {archiveCheckMode === 'past' ? 'Look-back' : 'Look-ahead'}
                      </label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {[7, 14].map((d) => (
                          <button
                            key={d}
                            onClick={() => {
                              setArchiveCheckDays(d);
                              refreshArchiveCheckPreview(d, archiveCheckClient, archiveCheckMode);
                            }}
                            style={{
                              padding: '5px 12px', fontSize: 12, borderRadius: 6, border: '1px solid',
                              borderColor: archiveCheckDays === d ? '#d97706' : 'var(--rh-border)',
                              background: archiveCheckDays === d ? '#fffbeb' : 'transparent',
                              color: archiveCheckDays === d ? '#d97706' : 'var(--rh-text-muted)',
                              cursor: 'pointer', fontWeight: archiveCheckDays === d ? 700 : 400,
                            }}
                            disabled={archiveCheckDispatching}
                          >
                            {d} days
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Preview counts */}
                  {!archiveCheckPreview ? (
                    <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading preview…</p>
                  ) : archiveCheckPreview.error ? (
                    <p style={{ fontSize: 13, color: '#dc2626' }}>{archiveCheckPreview.error}</p>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <span style={{ background: '#fffbeb', color: '#92400e', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {archiveCheckPreview.total_scheduled_comments} {archiveCheckMode === 'past' ? 'overdue' : 'scheduled'} comments
                        </span>
                        <span style={{ background: 'var(--rh-blue-bg)', color: 'var(--rh-blue)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {archiveCheckPreview.unique_post_urls} unique posts to check
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="13" height="13" fill="none" stroke="#16a34a" strokeWidth="2.4" viewBox="0 0 24 24">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                        <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                          Checking across all clients
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setShowArchiveCheckConfirm(false)} disabled={archiveCheckDispatching}>
                Cancel
              </button>
              <button
                className="rh-btn"
                style={{
                  background: archiveCheckDispatching || !archiveCheckPreview || archiveCheckPreview.error ? '#fcd34d' : '#d97706',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px',
                  fontWeight: 600, fontSize: 13,
                  cursor: archiveCheckDispatching || !archiveCheckPreview || archiveCheckPreview.error ? 'not-allowed' : 'pointer',
                }}
                onClick={confirmArchiveCheck}
                disabled={archiveCheckDispatching || !archiveCheckPreview || !!archiveCheckPreview.error}
              >
                {archiveCheckDispatching ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dispatching…
                  </span>
                ) : 'Run in Background'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Archive Check — floating running banner ── */}
      {archiveCheckTaskId && (
        <div style={{
          position: 'fixed', bottom: (livenessTaskId ? 76 : 0) + (backfillTaskId ? 76 : 0) + 24, right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          border: '1px solid rgba(217,119,6,0.25)',
          borderRadius: 14, padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(217,119,6,0.15)',
          display: 'flex', alignItems: 'center', gap: 12, minWidth: 240,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#d97706' }}>Archive check running…</div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 2 }}>You'll be notified when it's done</div>
          </div>
        </div>
      )}

      {/* ── Archive Check — result popup ── */}
      {archiveCheckTaskDone && (
        <div style={{
          position: 'fixed',
          bottom: (livenessTaskDone ? 220 : 0) + (backfillTaskDone ? 220 : 0) + 24,
          right: 24, zIndex: 200,
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px) saturate(190%)',
          WebkitBackdropFilter: 'blur(20px) saturate(190%)',
          border: archiveCheckTaskResult?.error ? '1px solid rgba(220,38,38,0.3)' : '1px solid rgba(217,119,6,0.3)',
          borderRadius: 16, padding: '18px 22px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          minWidth: 280, maxWidth: 340,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {archiveCheckTaskResult?.error ? (
                <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              ) : (
                <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: archiveCheckTaskResult?.error ? '#dc2626' : '#d97706' }}>
                {archiveCheckTaskResult?.error ? 'Check failed' : 'Archive check complete'}
              </span>
            </div>
            <button
              onClick={() => { setArchiveCheckTaskDone(false); setArchiveCheckTaskResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-muted)', lineHeight: 1 }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {archiveCheckTaskResult?.error ? (
            <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{archiveCheckTaskResult.error}</p>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: archiveCheckTaskResult?.inaccessible?.length ? 12 : 0 }}>
                {[
                  { label: 'Posts checked', value: archiveCheckTaskResult?.posts_checked ?? 0, color: '#6b7280' },
                  { label: 'Inaccessible', value: archiveCheckTaskResult?.posts_inaccessible ?? 0, color: archiveCheckTaskResult?.posts_inaccessible > 0 ? '#dc2626' : '#6b7280' },
                  { label: 'Archived', value: archiveCheckTaskResult?.comments_archived ?? 0, color: archiveCheckTaskResult?.comments_archived > 0 ? '#d97706' : '#6b7280' },
                  { label: 'Shuffled up', value: archiveCheckTaskResult?.comments_shuffled ?? 0, color: archiveCheckTaskResult?.comments_shuffled > 0 ? '#16a34a' : '#6b7280' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 1 }}>{label}</div>
                  </div>
                ))}
              </div>
              {archiveCheckTaskResult?.inaccessible?.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--rh-text-muted)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--rh-text)', display: 'block', marginBottom: 6 }}>Affected posts:</strong>
                  {archiveCheckTaskResult.inaccessible.map((item, i) => (
                    <div key={i} style={{ marginBottom: 8, padding: '7px 10px', background: 'rgba(255,255,255,0.5)', borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                          {item.reason}
                        </span>
                        {item.clients?.map((cl) => (
                          <span key={cl} style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600 }}>
                            {cl}
                          </span>
                        ))}
                      </div>
                      <span style={{ wordBreak: 'break-all', fontSize: 11 }}>{item.url}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {deleteTarget && (
        <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="rh-modal" style={{ maxWidth: 400, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title" style={{ color: '#dc2626' }}>Cancel Assignment</h3>
              <button className="rh-modal-close" onClick={() => setDeleteTarget(null)}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6"/>
                    <path d="M9 6V4h6v2"/>
                  </svg>
                </div>
                <div>
                  <p style={{ fontWeight: 600, color: 'var(--rh-text)', marginBottom: 6 }}>Cancel this assignment?</p>
                  <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.5 }}>
                    <strong>{deleteTarget.post_title || 'Untitled post'}</strong> will be marked Cancelled. It stays in the system so you can restore it, but it will not appear in the Redditor todo and will not be billed.
                    {deleteTarget.status === 'scheduled' ? ' Later scheduled dates for this client will shift earlier to fill the gap.' : ''}
                  </p>
                </div>
              </div>
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Keep assignment</button>
              <button
                className="rh-btn"
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1 }}
                onClick={confirmDeleteComment}
                disabled={deleting}
              >
                {deleting ? 'Cancelling…' : 'Cancel assignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Full Comment Modal */}
      {viewTarget && (
        <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && setViewTarget(null)}>
          <div className="rh-modal" style={{ maxWidth: 580, width: '95%' }}>
            <div className="rh-modal-header">
              <h3 className="rh-modal-title">Full Comment</h3>
              <button className="rh-modal-close" onClick={() => setViewTarget(null)}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Post</p>
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--rh-text)' }}>{viewTarget.post_title || '—'}</p>
              </div>
              {/* Meta row — Subreddit + Redditor + Post Link */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--rh-surface)', border: '1px solid var(--rh-border)', borderRadius: 20, padding: '4px 12px' }}>
                  <svg width="12" height="12" fill="none" stroke="var(--rh-text-muted)" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  <span style={{ fontSize: 12, color: 'var(--rh-text-muted)', fontWeight: 500 }}>
                    {viewTarget.subreddit ? (viewTarget.subreddit.startsWith('r/') ? viewTarget.subreddit : `r/${viewTarget.subreddit}`) : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--rh-surface)', border: '1px solid var(--rh-border)', borderRadius: 20, padding: '4px 12px' }}>
                  <svg width="12" height="12" fill="none" stroke="var(--rh-text-muted)" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span style={{ fontSize: 12, color: 'var(--rh-text-muted)', fontWeight: 500 }}>
                    {viewTarget.redditor_name || '—'}
                  </span>
                </div>
                {viewTarget.reddit_account_username ? (
                  <a
                    href={`https://www.reddit.com/user/${viewTarget.reddit_account_username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--rh-surface)', border: '1px solid var(--rh-border)', borderRadius: 20, padding: '4px 12px', textDecoration: 'none' }}
                  >
                    <svg width="12" height="12" fill="none" stroke="var(--rh-text-muted)" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                      <line x1="9" y1="9" x2="9.01" y2="9"/>
                      <line x1="15" y1="9" x2="15.01" y2="9"/>
                    </svg>
                    <span style={{ fontSize: 12, color: 'var(--rh-text-muted)', fontWeight: 500 }}>
                      u/{viewTarget.reddit_account_username}
                    </span>
                  </a>
                ) : (
                  <div
                    title="No Reddit account was linked to this comment"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--rh-surface)', border: '1px solid var(--rh-border)', borderRadius: 20, padding: '4px 12px' }}
                  >
                    <svg width="12" height="12" fill="none" stroke="var(--rh-text-muted)" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                      <line x1="9" y1="9" x2="9.01" y2="9"/>
                      <line x1="15" y1="9" x2="15.01" y2="9"/>
                    </svg>
                    <span style={{ fontSize: 12, color: 'var(--rh-text-muted)', fontWeight: 500 }}>
                      u/—
                    </span>
                  </div>
                )}
                {viewTarget.reddit_url && (() => {
                  const href = viewTarget.reddit_url.startsWith('http')
                    ? viewTarget.reddit_url
                    : `https://www.reddit.com${viewTarget.reddit_url}`;
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--rh-surface)', border: '1px solid var(--rh-border)', borderRadius: 20, padding: '4px 12px', textDecoration: 'none' }}
                    >
                      <svg width="12" height="12" fill="none" stroke="var(--rh-orange)" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                      <span style={{ fontSize: 12, color: 'var(--rh-orange)', fontWeight: 500 }}>View Post</span>
                    </a>
                  );
                })()}
              </div>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Comment Text</p>
                <div style={{
                  background: 'var(--rh-surface)',
                  border: '1px solid var(--rh-border)',
                  borderRadius: 8,
                  padding: '14px 16px',
                  fontSize: 14,
                  color: 'var(--rh-text)',
                  lineHeight: 1.65,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 360,
                  overflowY: 'auto',
                }}>
                  {viewTarget.comment_text}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 240 }}>
                  {[
                    ['Submitted at', viewTarget.submitted_at, viewTarget.submitted_at_is_approximate],
                    ['Live at', viewTarget.live_at, false],
                    ['Deleted at', viewTarget.deleted_at, false],
                  ].map(([label, value, approximate]) => (
                    <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: 96, flexShrink: 0 }}>
                        {label}
                      </p>
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text)', fontVariantNumeric: 'tabular-nums' }}>
                        {formatDateTime(value)}
                        {approximate && value ? (
                          <span
                            title="Approximate — derived from scheduled publish date"
                            style={{ marginLeft: 3, color: 'var(--rh-text-muted)', cursor: 'help', fontWeight: 700 }}
                          >
                            *
                          </span>
                        ) : null}
                      </p>
                    </div>
                  ))}
                </div>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Status</p>
                  <StatusBadge status={viewTarget.status} submittedUrl={viewTarget.submitted_url} skipReason={viewTarget.skip_reason} redditorNote={viewTarget.redditor_note} cancelledByName={viewTarget.cancelled_by_name} cancelledAt={viewTarget.cancelled_at} />
                </div>
              </div>
              {viewTarget.submitted_url && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Comment URL</p>
                  <a
                    href={viewTarget.submitted_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 13, color: 'var(--rh-orange)', wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    {viewTarget.submitted_url}
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                </div>
              )}
              {viewTarget.screenshot_url && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    Screenshot
                  </p>
                  <a href={viewTarget.screenshot_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                    <img
                      src={viewTarget.screenshot_url}
                      alt="Submission screenshot"
                      style={{
                        width: '100%', maxHeight: 320, objectFit: 'contain',
                        border: '1px solid var(--rh-border)', borderRadius: 8,
                        background: 'var(--rh-bg)', cursor: 'zoom-in',
                      }}
                    />
                    <p style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginTop: 4 }}>
                      Click to open full size
                    </p>
                  </a>
                </div>
              )}
              {viewTarget.status === 'skipped' && viewTarget.skip_reason && (
                <div style={{
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  padding: '12px 14px',
                }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Skip reason
                  </p>
                  <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, margin: 0 }}>
                    {SKIP_REASON_LABELS[viewTarget.skip_reason] || viewTarget.skip_reason}
                    {viewTarget.skipped_at && (
                      <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginLeft: 8 }}>
                        · {new Date(viewTarget.skipped_at).toLocaleString()}
                      </span>
                    )}
                  </p>
                </div>
              )}
              {viewTarget.status === 'cancelled' && (
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #94a3b8',
                  borderRadius: 8,
                  padding: '12px 14px',
                }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Cancelled
                  </p>
                  <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, margin: 0 }}>
                    {viewTarget.cancelled_by_name ? `Cancelled by ${viewTarget.cancelled_by_name}` : 'Cancelled by an admin'}
                    {viewTarget.cancelled_at && (
                      <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', marginLeft: 8 }}>
                        · {new Date(viewTarget.cancelled_at).toLocaleString()}
                      </span>
                    )}
                  </p>
                </div>
              )}
              {viewTarget.redditor_note && (
                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: 8,
                  padding: '12px 14px',
                }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Remarks
                  </p>
                  <p style={{ fontSize: 13, color: '#78350f', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                    {viewTarget.redditor_note}
                  </p>
                </div>
              )}
              {(viewTarget.device_type || viewTarget.browser_name || viewTarget.os_name) && (
                <div style={{
                  background: 'var(--rh-bg-subtle, #f8fafc)',
                  border: '1px solid var(--rh-border)',
                  borderRadius: 8,
                  padding: '12px 14px',
                }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    Device Info
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                    {viewTarget.device_type && (
                      <div>
                        <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', display: 'block' }}>Device</span>
                        <span style={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>{viewTarget.device_type}</span>
                      </div>
                    )}
                    {viewTarget.browser_name && (
                      <div>
                        <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', display: 'block' }}>Browser</span>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{viewTarget.browser_name}</span>
                      </div>
                    )}
                    {viewTarget.os_name && (
                      <div>
                        <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', display: 'block' }}>OS</span>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{viewTarget.os_name}</span>
                      </div>
                    )}
                    {viewTarget.device_detected_at && (
                      <div>
                        <span style={{ fontSize: 11, color: 'var(--rh-text-muted)', display: 'block' }}>Detected</span>
                        <span style={{ fontSize: 13 }}>{new Date(viewTarget.device_detected_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setViewTarget(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Posted Comment Modal (Submitted / Deleted / DWS) ── */}
      {postEditTarget && (() => {
        const fieldLabel = {
          fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
        };
        const inputStyle = {
          width: '100%', boxSizing: 'border-box',
          border: '1.5px solid var(--rh-border)', borderRadius: 8,
          padding: '9px 12px', fontSize: 13, color: 'var(--rh-text)',
          background: '#fff', outline: 'none', fontFamily: 'inherit',
        };
        return (
          <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && !postEditSaving && setPostEditTarget(null)}>
            <div className="rh-modal" style={{ maxWidth: 520, width: '95%' }}>
              <div className="rh-modal-header">
                <h3 className="rh-modal-title">{postEditMode === 'submit' ? 'Submit Comment' : 'Edit Posted Comment'}</h3>
                <button className="rh-modal-close" onClick={() => !postEditSaving && setPostEditTarget(null)}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <form onSubmit={savePostEdit}>
                <div className="rh-modal-body" style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* Read-only: post info */}
                  <div>
                    <p style={fieldLabel}>Post</p>
                    <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--rh-text)', margin: 0 }}>{postEditTarget.post_title || '—'}</p>
                    <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', margin: '2px 0 0' }}>{postEditTarget.client_name} · {postEditTarget.redditor_name}</p>
                  </div>

                  {/* Submitted URL — hidden when marking as skipped */}
                  {!(postEditMode === 'submit' && postEditFields.skipped) && (
                  <div>
                    <p style={fieldLabel}>
                      Submitted Comment URL
                      {postEditMode === 'submit' && postEditFields.deleted_while_submitting && (
                        <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--rh-text-faint)' }}> (optional for DWS)</span>
                      )}
                    </p>
                    <input
                      type="url"
                      placeholder="https://www.reddit.com/r/…/comment/…"
                      value={postEditFields.submitted_url}
                      onChange={(e) => setPostEditFields((f) => ({ ...f, submitted_url: e.target.value }))}
                      style={{ ...inputStyle, opacity: postEditFields.deleted_while_submitting ? 0.6 : 1 }}
                      disabled={postEditSaving || postEditLivenessRan}
                      required={postEditMode === 'submit' && !postEditFields.deleted_while_submitting && !postEditFields.skipped}
                    />
                    {postEditFields.submitted_url && (
                      <a
                        href={postEditFields.submitted_url}
                        target="_blank"
                        rel="noreferrer"
                        className="rh-link"
                        style={{ fontSize: 12, marginTop: 4, display: 'inline-block' }}
                      >
                        Open link ↗
                      </a>
                    )}
                  </div>
                  )}

                  {/* Account Used — hidden when marking as skipped */}
                  {!(postEditMode === 'submit' && postEditFields.skipped) && (
                  <div>
                    <p style={fieldLabel}>Account Used</p>
                    {postEditAccountsLoading ? (
                      <p style={{ fontSize: 13, color: 'var(--rh-text-muted)' }}>Loading accounts…</p>
                    ) : (
                      <select
                        value={postEditFields.reddit_account_id}
                        onChange={(e) => setPostEditFields((f) => ({ ...f, reddit_account_id: e.target.value }))}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                        disabled={postEditSaving || postEditLivenessRan}
                      >
                        <option value="">— Not specified —</option>
                        {postEditAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>u/{acc.username}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  )}

                  {/* DWS / Skip toggles (submit mode only) */}
                  {postEditMode === 'submit' && !postEditLivenessRan && (
                    <>
                    <label style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      background: postEditFields.deleted_while_submitting ? '#fff7ed' : 'var(--rh-surface)',
                      border: `1px solid ${postEditFields.deleted_while_submitting ? '#fed7aa' : 'var(--rh-border)'}`,
                      transition: 'background 0.15s, border-color 0.15s',
                    }}>
                      <input
                        type="checkbox"
                        checked={!!postEditFields.deleted_while_submitting}
                        onChange={(e) => setPostEditFields((f) => ({
                          ...f,
                          deleted_while_submitting: e.target.checked,
                          skipped: e.target.checked ? false : f.skipped,
                        }))}
                        disabled={postEditSaving}
                        style={{ marginTop: 2, cursor: 'pointer', accentColor: '#ea580c' }}
                      />
                      <span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--rh-text)', display: 'block' }}>
                          Deleted while submitting (DWS)
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>
                          Reddit removed the comment instantly. Marks it as an instant deletion — no liveness check is run.
                        </span>
                      </span>
                    </label>

                    <label style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      background: postEditFields.skipped ? '#f8fafc' : 'var(--rh-surface)',
                      border: `1px solid ${postEditFields.skipped ? '#cbd5e1' : 'var(--rh-border)'}`,
                      transition: 'background 0.15s, border-color 0.15s',
                    }}>
                      <input
                        type="checkbox"
                        checked={!!postEditFields.skipped}
                        onChange={(e) => setPostEditFields((f) => ({
                          ...f,
                          skipped: e.target.checked,
                          deleted_while_submitting: e.target.checked ? false : f.deleted_while_submitting,
                          skip_reason: e.target.checked ? f.skip_reason : '',
                        }))}
                        disabled={postEditSaving}
                        style={{ marginTop: 2, cursor: 'pointer', accentColor: '#64748b' }}
                      />
                      <span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--rh-text)', display: 'block' }}>
                          Mark as Skipped
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>
                          The comment will not be posted. Requires a skip reason — same as a redditor skip.
                        </span>
                      </span>
                    </label>
                    </>
                  )}

                  {postEditMode === 'submit' && !postEditLivenessRan && postEditFields.skipped && (
                    <>
                      <div>
                        <p style={fieldLabel}>Skip reason</p>
                        <select
                          value={postEditFields.skip_reason || ''}
                          onChange={(e) => setPostEditFields((f) => ({ ...f, skip_reason: e.target.value }))}
                          style={{ ...inputStyle, cursor: 'pointer' }}
                          disabled={postEditSaving}
                          required
                        >
                          <option value="">— Select a reason —</option>
                          {Object.entries(SKIP_REASON_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <p style={fieldLabel}>
                          Remarks
                          {postEditFields.skip_reason === 'other' && (
                            <span style={{ color: '#dc2626', marginLeft: 4, textTransform: 'none', letterSpacing: 0 }}>*</span>
                          )}
                        </p>
                        <textarea
                          placeholder={postEditFields.skip_reason === 'other'
                            ? 'Required — explain why this comment is being skipped'
                            : 'Optional extra context'}
                          value={postEditFields.redditor_note || ''}
                          onChange={(e) => setPostEditFields((f) => ({ ...f, redditor_note: e.target.value }))}
                          rows={3}
                          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                          disabled={postEditSaving}
                          required={postEditFields.skip_reason === 'other'}
                        />
                      </div>
                    </>
                  )}

                  {/* Liveness result notice */}
                  {postEditLivenessRan && (
                    postEditWasSkipped ? (
                      <div style={{
                        background: '#f8fafc', border: '1px solid #cbd5e1',
                        borderRadius: 8, padding: '10px 14px',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <svg width="16" height="16" fill="none" stroke="#475569" strokeWidth="2.2" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/>
                        </svg>
                        <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
                          Marked as Skipped.
                        </span>
                      </div>
                    ) : postEditWasDws ? (
                      <div style={{
                        background: '#fff7ed', border: '1px solid #fed7aa',
                        borderRadius: 8, padding: '10px 14px',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <svg width="16" height="16" fill="none" stroke="#ea580c" strokeWidth="2.2" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                        <span style={{ fontSize: 13, color: '#c2410c', fontWeight: 600 }}>
                          Marked as Deleted While Submitting (instant deletion).
                        </span>
                      </div>
                    ) : (
                    postEditLivenessDeleted ? (
                      <div style={{
                        background: '#fef2f2', border: '1px solid #fecaca',
                        borderRadius: 8, padding: '10px 14px',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <svg width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                        <span style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>
                          Submitted & liveness check run — the comment came back as not live (deleted).
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        background: '#f0fdf4', border: '1px solid #bbf7d0',
                        borderRadius: 8, padding: '10px 14px',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2.2" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                        </svg>
                        <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                          {postEditMode === 'submit'
                            ? 'Submitted & liveness check run — status updated.'
                            : 'Saved & liveness check run — status updated based on the new URL.'}
                        </span>
                      </div>
                    )
                    )
                  )}
                </div>
                <div className="rh-modal-footer">
                  <button type="button" className="rh-btn rh-btn-secondary" onClick={() => setPostEditTarget(null)} disabled={postEditSaving}>
                    {postEditLivenessRan ? 'Close' : 'Cancel'}
                  </button>
                  {!postEditLivenessRan && (
                    <button
                      type="submit"
                      className="rh-btn rh-btn-primary"
                      disabled={
                        postEditSaving
                        || (postEditMode === 'submit' && !!postEditFields.skipped && (
                          !postEditFields.skip_reason
                          || (postEditFields.skip_reason === 'other' && !(postEditFields.redditor_note || '').trim())
                        ))
                      }
                      style={{ opacity: postEditSaving ? 0.7 : 1, cursor: postEditSaving ? 'not-allowed' : 'pointer' }}
                    >
                      {postEditSaving
                        ? (postEditMode === 'submit' ? 'Submitting…' : 'Saving…')
                        : (postEditMode === 'submit'
                            ? (postEditFields.skipped
                                ? 'Mark as Skipped'
                                : (postEditFields.deleted_while_submitting ? 'Mark as DWS' : 'Submit & Run Liveness'))
                            : 'Save Changes')}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Edit Comment Modal */}
      {editTarget && (() => {
        const today = new Date().toISOString().slice(0, 10);
        const postHref = editTarget.reddit_url?.startsWith('http')
          ? editTarget.reddit_url
          : `https://www.reddit.com${editTarget.reddit_url || ''}`;
        const fieldLabel = {
          fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
        };
        const readBox = {
          background: 'var(--rh-surface)', border: '1px solid var(--rh-border)',
          borderRadius: 8, padding: '10px 14px', fontSize: 14,
          color: 'var(--rh-text)', lineHeight: 1.55,
        };
        return (
          <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && !editSaving && setEditTarget(null)}>
            <div className="rh-modal" style={{ maxWidth: 580, width: '95%' }}>
              <div className="rh-modal-header">
                <h3 className="rh-modal-title">Edit Comment</h3>
                <button className="rh-modal-close" onClick={() => !editSaving && setEditTarget(null)}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <form onSubmit={saveEdit}>
                <div className="rh-modal-body" style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>

                  {/* Read-only: Post Title */}
                  <div>
                    <p style={fieldLabel}>Post</p>
                    <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--rh-text)' }}>{editTarget.post_title || '—'}</p>
                  </div>

                  {/* Read-only: Thread URL */}
                  <div>
                    <p style={fieldLabel}>Reddit Thread URL</p>
                    <div style={readBox}>
                      <a href={postHref} target="_blank" rel="noreferrer" className="rh-link" style={{ fontSize: 13, wordBreak: 'break-all' }}>
                        {editTarget.reddit_url || '—'}
                      </a>
                    </div>
                  </div>

                  {/* Editable: Schedule Date */}
                  <div>
                    <p style={fieldLabel}>Schedule Date</p>
                    <input
                      type="date"
                      min={today}
                      value={editFields.publishing_date}
                      onChange={(e) => setEditFields((f) => ({ ...f, publishing_date: e.target.value }))}
                      required
                      style={{
                        ...readBox,
                        width: '100%', boxSizing: 'border-box',
                        cursor: 'pointer', outline: 'none',
                        fontFamily: 'inherit', fontSize: 14,
                        appearance: 'none', WebkitAppearance: 'none',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--rh-border)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>

                  {/* Editable: Comment Text */}
                  <div>
                    <p style={fieldLabel}>Comment Text</p>
                    <textarea
                      value={editFields.comment_text}
                      onChange={(e) => setEditFields((f) => ({ ...f, comment_text: e.target.value }))}
                      rows={8}
                      required
                      style={{
                        ...readBox,
                        width: '100%', boxSizing: 'border-box',
                        resize: 'vertical', fontFamily: 'inherit',
                        lineHeight: 1.65, outline: 'none',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--rh-border)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>

                </div>
                <div className="rh-modal-footer">
                  <button type="button" className="rh-btn rh-btn-secondary" onClick={() => !editSaving && setEditTarget(null)} disabled={editSaving}>Cancel</button>
                  <button
                    type="submit"
                    className="rh-btn rh-btn-primary"
                    disabled={editSaving}
                    style={{ opacity: editSaving ? 0.7 : 1, cursor: editSaving ? 'not-allowed' : 'pointer' }}
                  >
                    {editSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {showCSVModal && (
        <CSVUploadModal
          onClose={() => setShowCSVModal(false)}
          onUploaded={() => { fetchComments(true); onRefresh?.(); }}
        />
      )}

      <div className="rh-page-header rh-overview-page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 className="rh-page-title">
            {selectedClient ? (
              <>{selectedClient} <span style={{ fontWeight: 400, color: 'var(--rh-text-muted)' }}>— Overview</span></>
            ) : 'Campaign Overview'}
          </h2>
          {selectedClient ? (
            <p className="rh-page-subtitle">
              Campaign assignments and stats for {selectedClient}
              {assignedRedditor ? (
                <>
                  {' · '}
                  <span style={{ color: 'var(--rh-text)', fontWeight: 600 }}>
                    Assigned: {assignedRedditor}
                  </span>
                </>
              ) : null}
            </p>
          ) : (
            <p className="rh-page-subtitle">All-time stats across all clients, redditors, and subreddits</p>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div className="rh-overview-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {isDeveloper && (
              <div ref={actionsMenuRef} style={{ position: 'relative' }}>
                <button
                  className="rh-btn"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, padding: '7px 14px',
                    background: '#f0f4ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                    borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                  }}
                  onClick={() => setShowActionsMenu((v) => !v)}
                  title="Developer tools"
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                  </svg>
                  Actions
                  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ marginLeft: 1, transition: 'transform 0.15s', transform: showActionsMenu ? 'rotate(180deg)' : 'none' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {showActionsMenu && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                    background: 'rgba(255,255,255,0.55)',
                    backdropFilter: 'blur(16px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                    border: '1px solid rgba(255,255,255,0.45)',
                    borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 1.5px 4px rgba(0,0,0,0.08)',
                    minWidth: 190, zIndex: 50, overflow: 'hidden',
                  }}>
                    <div style={{ padding: '6px 0' }}>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: 'var(--rh-text)', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(29,78,216,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openLivenessConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#1d4ed8" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        Liveness Check
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#6d28d9', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(109,40,217,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openBackfillConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#6d28d9" strokeWidth="2.2" viewBox="0 0 24 24">
                          <polyline points="1 4 1 10 7 10"/>
                          <path d="M3.51 15a9 9 0 1 0 .49-3"/>
                        </svg>
                        Fix Data
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#b45309', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(180,83,9,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openSubmittedAtBackfillConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#b45309" strokeWidth="2.2" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        Backfill Submitted At
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#0f766e', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(15,118,110,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openRecheckConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#0f766e" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M9 12l2 2 4-4"/>
                          <path d="M21 12a9 9 0 1 1-2.64-6.36"/>
                        </svg>
                        Recheck Accounts
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#9f1239', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(159,18,57,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openBannedCheckConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#9f1239" strokeWidth="2.2" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                        </svg>
                        Check Banned Accounts
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#be185d', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(190,24,93,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openCheckNicheConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#be185d" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M20.59 13.41L11 3.83V3H4v7h.83l9.58 9.59a2 2 0 0 0 2.83 0l3.35-3.35a2 2 0 0 0 0-2.83z"/>
                          <circle cx="7.5" cy="7.5" r="1.5" fill="#be185d" stroke="none"/>
                        </svg>
                        Check Niche
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#4338ca', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(67,56,202,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openSyncNocodbConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#4338ca" strokeWidth="2.2" viewBox="0 0 24 24">
                          <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                        </svg>
                        Sync Client Data
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#b91c1c', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(185,28,28,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openFillQuotaConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#b91c1c" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M4 19V5" />
                          <path d="M4 19h16" />
                          <path d="M8 15v-4" />
                          <path d="M12 15V7" />
                          <path d="M16 15v-6" />
                        </svg>
                        Fill Quota Shortfalls
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#d97706', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(217,119,6,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openArchiveCheckConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#d97706" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                        Archive Check
                      </button>
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#7c3aed', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={openSendRemindersConfirm}
                      >
                        <svg width="15" height="15" fill="none" stroke="#7c3aed" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3z"/>
                          <path d="M9 17v1a3 3 0 0 0 6 0v-1"/>
                        </svg>
                        Send Reminders
                      </button>
                      {assignedRedditorId && (
                        <button
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                            padding: '9px 16px', background: 'none', border: 'none',
                            cursor: 'pointer', fontSize: 13, color: '#b45309', textAlign: 'left',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(180,83,9,0.08)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          onClick={openResetOverdueConfirm}
                        >
                          <svg width="15" height="15" fill="none" stroke="#b45309" strokeWidth="2.2" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          Reset Overdue
                        </button>
                      )}
                      <div style={{ height: 1, background: 'rgba(0,0,0,0.08)', margin: '4px 0' }} />
                      <button
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 16px', background: 'none', border: 'none',
                          cursor: clearing ? 'not-allowed' : 'pointer', fontSize: 13, color: '#dc2626', textAlign: 'left',
                          opacity: clearing ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => { if (!clearing) e.currentTarget.style.background = 'rgba(220,38,38,0.08)'; }}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={() => { if (!clearing) { setShowActionsMenu(false); setShowClearConfirm(true); } }}
                        disabled={clearing}
                      >
                        <svg width="15" height="15" fill="none" stroke="#dc2626" strokeWidth="2.2" viewBox="0 0 24 24">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                        {clearing ? 'Clearing…' : 'Delete All'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {selectedClient && (
              <div ref={shareDropRef} style={{ position: 'relative' }}>
                <button
                  className="rh-btn rh-btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, padding: '7px 14px' }}
                  onClick={handleGetShareLink}
                  disabled={shareFetching}
                >
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  {shareFetching ? 'Getting link…' : 'Shareable Report'}
                </button>

                {shareToken && shareDropOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                    background: 'rgba(255,255,255,0.88)',
                    backdropFilter: 'blur(24px) saturate(2)',
                    WebkitBackdropFilter: 'blur(24px) saturate(2)',
                    border: '1px solid rgba(255,255,255,0.9)',
                    borderRadius: 16, padding: '14px 14px 12px',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1), inset 0 -1px 0 rgba(0,0,0,0.04)',
                    zIndex: 30, minWidth: 310,
                  }}>
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Shareable Link
                      </span>
                      <button
                        onClick={() => setShareDropOpen(false)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--rh-text-faint)', display: 'flex', alignItems: 'center' }}
                        title="Close"
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>

                    {/* URL row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 9, padding: '7px 10px', marginBottom: 10 }}>
                      <span style={{ flex: 1, fontSize: 11, color: 'var(--rh-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {shareToken.full_url}
                      </span>
                      {/* Open in new tab */}
                      <a
                        href={shareToken.full_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Open link"
                        style={{ color: 'var(--rh-text-muted)', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                      >
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      </a>
                    </div>

                    {/* Action buttons row */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <button
                        onClick={handleCopyShareLink}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          background: shareCopied ? 'rgba(21,128,61,0.08)' : 'rgba(255,69,0,0.08)',
                          color: shareCopied ? '#15803d' : 'var(--rh-orange)',
                          border: `1px solid ${shareCopied ? 'rgba(21,128,61,0.2)' : 'rgba(255,69,0,0.2)'}`,
                          transition: 'all 0.15s',
                        }}
                      >
                        {shareCopied ? (
                          <>
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                              <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                            Copy Link
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleRegenerateShareLink}
                        disabled={shareRegenerating}
                        title="Generate a new URL — the old link will stop working"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          padding: '7px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          background: 'none', color: 'var(--rh-text-muted)',
                          border: '1px solid var(--rh-border)',
                          opacity: shareRegenerating ? 0.6 : 1,
                        }}
                      >
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <polyline points="23 4 23 10 17 10"/>
                          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        {shareRegenerating ? 'Regenerating…' : 'Regenerate'}
                      </button>
                    </div>

                    <p style={{
                      margin: 0, fontSize: 10.5, lineHeight: 1.5,
                      color: 'rgba(0,0,0,0.38)', textAlign: 'center',
                    }}>
                      This is a permanent link. It will only stop working if you hit <strong style={{ fontWeight: 600 }}>Regenerate</strong>.
                    </p>
                  </div>
                )}
              </div>
            )}
            <button
              className="rh-btn rh-btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, padding: '7px 14px' }}
              onClick={() => setShowCSVModal(true)}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <polyline points="16 16 12 12 8 16"/>
                <line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
              </svg>
              Add Comments
            </button>
            <LiveBadge />
          </div>
          {lastUpdatedStr && (
            <span style={{ fontSize: 11, color: 'var(--rh-text-faint)' }}>Updated {lastUpdatedStr}</span>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className="rh-kpi-row rh-kpi-row-5">

        {/* Total Assigned */}
        <div className="rh-kpi-card rh-kpi-card--default">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value default">{loading ? '—' : ov.total ?? 0}</p>
            <p className="rh-kpi-label">Total Assigned</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--default">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
        </div>

        {/* Submitted */}
        <div className="rh-kpi-card rh-kpi-card--green">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value green">{loading ? '—' : ov.submitted ?? 0}</p>
            <p className="rh-kpi-label">Submitted</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--green">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
        </div>

        {/* Scheduled (excludes overdue) */}
        <div className="rh-kpi-card rh-kpi-card--yellow">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value yellow">{loading ? '—' : ov.scheduled ?? 0}</p>
            <p className="rh-kpi-label">Scheduled</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--yellow">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M5 22h14"/><path d="M5 2h14"/>
              <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/>
              <path d="M7 2v4.172a2 2 0 0 1 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>
            </svg>
          </div>
        </div>

        {/* Overdue */}
        <div className="rh-kpi-card rh-kpi-card--orange">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value orange">{loading ? '—' : ov.overdue ?? 0}</p>
            <p className="rh-kpi-label">Overdue</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--orange">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
        </div>

        {/* Deleted */}
        <div className="rh-kpi-card rh-kpi-card--red">
          <div className="rh-kpi-body">
            <p className="rh-kpi-value red">{loading ? '—' : ov.deleted ?? 0}</p>
            <p className="rh-kpi-label">Deleted</p>
          </div>
          <div className="rh-kpi-icon-wrap rh-kpi-icon--red">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </div>
        </div>

      </div>

      {/* All Assignments + Recent Activity */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

      {/* All Assignments */}
      <div className="rh-card" style={{ overflow: 'visible' }}>
        <div className="rh-card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3 className="rh-card-title">All Assignments</h3>
            {!loadingComments && (
              <span style={{
                fontSize: 12, fontWeight: 600,
                background: 'var(--rh-orange-light)',
                color: 'var(--rh-orange)',
                border: '1px solid rgba(255,69,0,0.2)',
                borderRadius: 20, padding: '2px 10px',
              }}>
                {filteredComments.length} of {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
              </span>
            )}
            {!loadingComments && filteredComments.length > 0 && (
              <button
                type="button"
                onClick={exportAssignmentsCSV}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: 'none', border: '1px solid var(--rh-border)', borderRadius: 8,
                  padding: '4px 12px', fontSize: 12, color: 'var(--rh-text-muted)',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
            )}
          </div>
          <div className="rh-filter-row" style={{ flexWrap: 'wrap', gap: 8 }}>
            {/* Date range */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <RhDatePicker value={dateFrom} onChange={setDateFrom} placeholder="From date" />
              <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>—</span>
              <RhDatePicker value={dateTo} onChange={setDateTo} placeholder="To date" />
            </div>
            <button
              type="button"
              onClick={openPullAssignmentsConfirm}
              title={clientFilter ? `Pull future scheduled into this month for ${clientFilter}` : 'Select a client first'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: 'none',
                border: '1px solid var(--rh-border)',
                borderRadius: 8,
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 500,
                color: clientFilter ? 'var(--rh-text-muted)' : 'var(--rh-text-faint)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                opacity: clientFilter ? 1 : 0.65,
              }}
            >
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 5v14"/><path d="M5 12h14"/>
              </svg>
              Pull Assignments
            </button>
            {/* Multi-checkbox status filter */}
            <div ref={statusDropRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setStatusDropOpen(o => !o)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  border: '1px solid var(--rh-border)', borderRadius: 8,
                  padding: '6px 12px', fontSize: 13, fontWeight: 500,
                  background: '#fff', cursor: 'pointer', color: 'var(--rh-text)',
                  whiteSpace: 'nowrap', minWidth: 150,
                }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <polyline points="9 11 12 14 22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
                <span style={{ flex: 1, textAlign: 'left' }}>
                  {activeStatuses.size === 0
                    ? 'No Statuses'
                    : activeStatuses.size === STATUS_OPTIONS.length
                    ? 'All Statuses'
                    : `${activeStatuses.size} Selected`}
                </span>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <polyline points={statusDropOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}/>
                </svg>
              </button>

              {statusDropOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  background: '#fff', border: '1px solid var(--rh-border)',
                  borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 200, minWidth: 200, padding: '8px 0',
                }}>
                  {/* Select all / Clear */}
                  <div style={{
                    display: 'flex', gap: 6, padding: '4px 12px 8px',
                    borderBottom: '1px solid var(--rh-border)',
                  }}>
                    <button
                      onClick={() => setActiveStatuses(new Set(STATUS_OPTIONS.map(o => o.value)))}
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--rh-orange)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Select all
                    </button>
                    <span style={{ color: 'var(--rh-border)' }}>·</span>
                    <button
                      onClick={() => setActiveStatuses(new Set())}
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--rh-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Clear
                    </button>
                    <span style={{ color: 'var(--rh-border)' }}>·</span>
                    <button
                      onClick={() => setActiveStatuses(new Set(DEFAULT_ACTIVE))}
                      style={{ fontSize: 11, fontWeight: 600, color: 'var(--rh-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Reset
                    </button>
                  </div>

                  {STATUS_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px', cursor: 'pointer', fontSize: 13,
                        color: 'var(--rh-text)',
                        background: activeStatuses.has(opt.value) ? 'rgba(255,69,0,0.04)' : 'transparent',
                        transition: 'background 0.1s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={activeStatuses.has(opt.value)}
                        onChange={() => toggleStatus(opt.value)}
                        style={{ accentColor: 'var(--rh-orange)', width: 15, height: 15, cursor: 'pointer' }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="rh-table-wrap" style={{ borderRadius: '0 0 var(--rh-radius) var(--rh-radius)', overflowX: 'auto' }}>
          <table className="rh-table" style={{ tableLayout: 'fixed', width: '100%', minWidth: isAdmin ? 1320 : undefined }}>
            <colgroup>
              <col style={{ width: isAdmin ? 'calc(22% - 10px)' : 'calc(30% - 10px)' }} />
              {isAdmin && <col style={{ width: 140 }} />}
              <col style={{ width: 160 }} />
              <col style={{ width: 135 }} />
              <col />
              {isAdmin && <col style={{ width: 140 }} />}
              <col style={{ width: 130 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Post Title</th>
                {isAdmin && <th>Redditor</th>}
                <th>Subreddit</th>
                <th style={{ textAlign: 'center' }}>Thread Created</th>
                <th>Comment Preview</th>
                {isAdmin && <th>Account Used</th>}
                <th style={{ textAlign: 'center' }}>Comment Date</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingComments ? (
                <tr><td colSpan={isAdmin ? 9 : 7} className="rh-empty">Loading…</td></tr>
              ) : filteredComments.length === 0 ? (
                <tr><td colSpan={isAdmin ? 9 : 7} className="rh-empty">No assignments match the selected filters.</td></tr>
              ) : filteredComments.map((c) => {
                const postHref = c.reddit_url?.startsWith('http')
                  ? c.reddit_url
                  : `https://www.reddit.com${c.reddit_url || ''}`;
                const isScheduled = c.status === 'scheduled';
                const subredditLabel = c.subreddit
                  ? (c.subreddit.startsWith('r/') ? c.subreddit : `r/${c.subreddit}`)
                  : '—';
                const accountUsername = postedAccountUsername(c);
                return (
                  <tr key={c.id}>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a className="rh-link" href={postHref} target="_blank" rel="noreferrer" title={c.post_title}>
                        {c.post_title}
                      </a>
                    </td>
                    {isAdmin && (
                      <td className="rh-text-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.redditor_name || ''}>
                        {c.redditor_name || '—'}
                      </td>
                    )}
                    <td className="rh-text-sm rh-text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {subredditLabel}
                    </td>
                    <td className="rh-text-sm" style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>{c.post_date || '—'}</td>
                    <td className="rh-text-muted rh-text-sm" style={{ overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                          {c.comment_text}
                        </span>
                        <button
                          onClick={() => setViewTarget(c)}
                          style={{
                            flexShrink: 0,
                            background: 'var(--rh-orange)', border: 'none', padding: '2px 8px',
                            borderRadius: 20, cursor: 'pointer',
                            fontSize: 11, color: '#fff', fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          View full
                        </button>
                      </div>
                    </td>
                    {isAdmin && (
                      <td
                        className="rh-text-sm"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {accountUsername ? (
                          <a
                            className="rh-link"
                            href={`https://www.reddit.com/user/${accountUsername}`}
                            target="_blank"
                            rel="noreferrer"
                            title={`u/${accountUsername}`}
                          >
                            u/{accountUsername}
                          </a>
                        ) : '—'}
                      </td>
                    )}
                    <td className="rh-text-sm" style={{ whiteSpace: 'nowrap', textAlign: 'center', color: c.is_overdue ? '#c2410c' : (c.status === 'scheduled' ? '#1e40af' : undefined), fontWeight: c.is_overdue ? 600 : undefined }}>
                      {c.publishing_date}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <StatusBadge status={c.status} isOverdue={c.is_overdue} submittedUrl={c.submitted_url} skipReason={c.skip_reason} redditorNote={c.redditor_note} cancelledByName={c.cancelled_by_name} cancelledAt={c.cancelled_at} />
                      <ThreadHealth comment={c} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                        {(isDeveloper || isAdmin) && !['submitted', 'live', 'cancelled'].includes(c.status) && (
                          <button
                            title="Mark as submitted"
                            onClick={() => openPostEdit(c, 'submit')}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: 4, borderRadius: 6, color: 'var(--rh-text-faint)',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'color 0.15s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#16a34a'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--rh-text-faint)'}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                          </button>
                        )}
                        {isScheduled && (
                          <button
                            title="Edit comment"
                            onClick={() => openEdit(c)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: 4, borderRadius: 6, color: 'var(--rh-text-faint)',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'color 0.15s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--rh-orange)'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--rh-text-faint)'}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                        )}
                        {['submitted', 'deleted', 'dws'].includes(c.status) && (
                          <button
                            title="Edit posted comment"
                            onClick={() => openPostEdit(c)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: 4, borderRadius: 6, color: 'var(--rh-text-faint)',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'color 0.15s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--rh-text-faint)'}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                        )}
                        {['scheduled', 'skipped'].includes(c.status) && (
                        <button
                          title="Cancel assignment"
                          onClick={() => setDeleteTarget(c)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 4, borderRadius: 6, color: 'var(--rh-text-faint)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'color 0.15s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#dc2626'}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--rh-text-faint)'}
                        >
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4h6v2"/>
                          </svg>
                        </button>
                        )}
                        {c.status === 'cancelled' && (
                        <button
                          title="Restore assignment"
                          onClick={() => restoreCancelledComment(c)}
                          disabled={restoringId === c.id}
                          style={{
                            background: 'none', border: 'none',
                            cursor: restoringId === c.id ? 'not-allowed' : 'pointer',
                            padding: 4, borderRadius: 6, color: 'var(--rh-text-faint)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'color 0.15s',
                            opacity: restoringId === c.id ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => { if (restoringId !== c.id) e.currentTarget.style.color = '#2563eb'; }}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--rh-text-faint)'}
                        >
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <polyline points="1 4 1 10 7 10"/>
                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                          </svg>
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


      </div>{/* end flex wrapper */}
    </>
  );
}
